/*!
 * Geckio — © Panthure ApS (CVR 45983331). All rights reserved.
 * 
 * This theme is licensed, not sold. A licence covers ONE live Shopify store and is valid
 * only when the theme was purchased from geckio.io. It may not be resold, redistributed,
 * sub-licensed, or used to build stores for third parties without a licence per store.
 * 
 * Modifying it for the store it is licensed to is expressly allowed and encouraged.
 * 
 * geckio.io · support@geckio.io
 */
/* ============================================================
   Geckio — theme.js
   Tiny, dependency-free progressive enhancers. Loaded deferred.
   Everything degrades gracefully without JS and respects
   prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Recently viewed storage keys. These MUST live at the very top: init() runs
     further down but still inside this IIFE, so a `var` declared below it would
     still be `undefined` when recordRecentlyViewed() first reads it. */
  var RV_KEY = 'geckio:recently-viewed';
  var RV_CAP = 12;

  /* ---------- small helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function trapFocus(container, event) {
    var nodes = $all(FOCUSABLE, container).filter(function (el) {
      // tabindex="-1" is "focusable by script, not by Tab" — e.g. the predictive
      // search options, which are driven by arrow keys. Counting them would put
      // the wrap-around on an element Tab can never actually reach, and let focus
      // escape the panel.
      if (el.tabIndex < 0) return false;
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- make the page behind an open modal inert ---------- */
  function setBackgroundInert(on) {
    var regions = [document.getElementById('MainContent'), document.querySelector('footer.footer-group')];
    regions.forEach(function (el) {
      if (!el) return;
      if (on) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      } else {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      }
    });
  }

  /* ---------- header on scroll: shadow / transparent -> solid / hide on scroll down ----------
     data-sticky is the merchant setting: "always" | "scroll-up" | "none".
     The scroll listener is bound once and re-points at the current header, so
     re-rendering the section in the theme editor never stacks listeners. */
  var headerScroll = { el: null, root: null, mode: 'none', lastY: 0, bound: false, ticking: false };

  function updateHeaderScroll() {
    headerScroll.ticking = false;
    var header = headerScroll.el;
    if (!header || !header.isConnected) return;

    var y = window.scrollY || window.pageYOffset || 0;
    header.classList.toggle('is-scrolled', y > 4);

    var root = headerScroll.root;
    if (!root) { headerScroll.lastY = y; return; }

    if (headerScroll.mode !== 'scroll-up') {
      root.classList.remove('is-hidden');
      headerScroll.lastY = y;
      return;
    }

    // Never hide while a drawer is open, while focus is inside the header,
    // or while a mega-menu panel is open.
    var keepVisible =
      document.body.classList.contains('no-scroll') ||
      header.contains(document.activeElement) ||
      !!header.querySelector('.geckio-mega.is-open');

    var reveal = header.offsetHeight + 40;
    var delta = y - headerScroll.lastY;

    if (keepVisible || y <= reveal) {
      root.classList.remove('is-hidden');
    } else if (delta > 4) {
      root.classList.add('is-hidden');
    } else if (delta < -4) {
      root.classList.remove('is-hidden');
    }
    headerScroll.lastY = y;
  }

  function initStickyHeader() {
    var header = $('[data-header]');
    if (!header) return;

    headerScroll.el = header;
    headerScroll.root = header.closest('.shopify-section') || header.parentElement || header;
    headerScroll.mode = header.getAttribute('data-sticky') || 'none';
    headerScroll.lastY = window.scrollY || window.pageYOffset || 0;

    if (!headerScroll.bound) {
      headerScroll.bound = true;
      window.addEventListener('scroll', function () {
        if (headerScroll.ticking) return;
        headerScroll.ticking = true;
        window.requestAnimationFrame(updateHeaderScroll);
      }, { passive: true });
    }
    updateHeaderScroll();
  }

  /* ---------- header height -> CSS var (prevents layout shift) ---------- */
  function syncHeaderHeight() {
    var header = $('[data-header]');
    if (!header) return;
    var last = 0;
    function set() {
      var h = header.offsetHeight;
      // Guard against ResizeObserver feedback loops: only write when it really changed.
      if (Math.abs(h - last) < 1) return;
      last = h;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    set();
    if ('ResizeObserver' in window) {
      new ResizeObserver(set).observe(header);
    } else {
      window.addEventListener('resize', set, { passive: true });
    }
  }

  /* ---------- generic open/close panel (drawer + search) ---------- */
  function Panel(opts) {
    var panel = opts.panel;
    var openers = opts.openers || [];
    var closers = opts.closers || [];
    var overlay = opts.overlay || null;
    var onOpen = opts.onOpen;
    var lastFocused = null;
    var isOpen = false;

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      panel.hidden = false;
      // force reflow so the transition runs from the hidden state
      void panel.offsetWidth;
      panel.classList.add('is-open');
      document.body.classList.add('no-scroll');
      setBackgroundInert(true);
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
      if (typeof onOpen === 'function') onOpen(panel);
      var focusTarget = panel.querySelector('[data-autofocus]') || panel.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      panel.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
      setBackgroundInert(false);
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      var done = function () {
        panel.hidden = true;
        panel.removeEventListener('transitionend', done);
      };
      if (reduceMotion.matches) {
        done();
      } else {
        panel.addEventListener('transitionend', done);
        // safety net if transitionend never fires
        window.setTimeout(done, 400);
      }
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openers.forEach(function (o) {
      o.setAttribute('aria-expanded', 'false');
      o.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });
    closers.forEach(function (c) {
      c.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });
    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') trapFocus(panel, e);
    });

    return { open: open, close: close };
  }

  function initMenuDrawer() {
    var panel = $('[data-drawer="menu"]');
    if (!panel) return;
    Panel({
      panel: panel,
      openers: $all('[data-drawer-open="menu"]'),
      closers: $all('[data-drawer-close="menu"]'),
      overlay: $('[data-drawer-overlay="menu"]')
    });
  }

  /* ---------- predictive search (header) ----------
     Suggestions are rendered server-side through the Section Rendering API
     (/search/suggest?…&section_id=predictive-search), so images, titles and money
     formatting come from Liquid — the client never invents a price or a row.

     Reuses Panel() so the overlay traps focus, locks body scroll, inerts the page
     behind it and closes on Escape exactly like the menu and cart drawers.
     The opener stays a real <a href="/search">, so with JS off the icon simply
     goes to the search page. */
  function initPredictiveSearch() {
    var root = $('[data-search]');
    if (!root || root.dataset.searchReady === 'true') return;

    var input = $('[data-search-input]', root);
    var listbox = $('[data-search-listbox]', root);
    var messageBox = $('[data-search-empty]', root);
    var hint = $('[data-search-hint]', root);
    var status = $('[data-search-status]', root);
    var clearBtn = $('[data-search-clear]', root);
    var toggles = $all('[data-search-toggle]');
    if (!input || !listbox || !toggles.length) return;

    root.dataset.searchReady = 'true';

    var suggestUrl = root.getAttribute('data-search-url') || '/search/suggest';
    var types = root.getAttribute('data-search-types') || 'product';
    var limit = parseInt(root.getAttribute('data-search-limit'), 10) || 6;
    var minChars = parseInt(root.getAttribute('data-search-min'), 10) || 2;
    var wordText = root.getAttribute('data-search-word') || '';
    var noneText = root.getAttribute('data-search-none') || '';
    var errorText = root.getAttribute('data-search-error') || '';

    var options = [];
    var activeIndex = -1;
    var controller = null;
    var timer = null;
    var token = 0;
    var cache = {};

    function announce(msg) {
      if (status && status.textContent !== msg) status.textContent = msg;
    }

    function setExpanded(on) {
      input.setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    function clearActive() {
      var el = options[activeIndex];
      if (el) {
        el.classList.remove('is-active');
        el.setAttribute('aria-selected', 'false');
      }
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    }

    function setActive(i) {
      if (!options.length) return;
      clearActive();
      if (i < 0) i = options.length - 1;
      if (i > options.length - 1) i = 0;
      activeIndex = i;
      var el = options[i];
      el.classList.add('is-active');
      el.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', el.id);
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    /* Empty the popup. showHint=true is the idle state (query too short). */
    function reset(showHint) {
      clearActive();
      options = [];
      listbox.innerHTML = '';
      if (messageBox) { messageBox.innerHTML = ''; messageBox.hidden = true; }
      if (hint) hint.hidden = !showHint;
      root.classList.remove('is-loading');
      setExpanded(false);
      announce('');
    }

    function showMessage(text) {
      reset(false);
      if (!messageBox) return;
      messageBox.textContent = text;
      messageBox.hidden = false;
      announce(text);
    }

    function apply(html) {
      root.classList.remove('is-loading');

      var incoming = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-predictive-results]');

      if (!incoming) { reset(false); return; }

      var count = parseInt(incoming.getAttribute('data-search-count'), 10) || 0;
      var optionsSrc = incoming.querySelector('[data-predictive-options]');
      var emptySrc = incoming.querySelector('[data-predictive-empty]');

      clearActive();
      listbox.innerHTML = optionsSrc ? optionsSrc.innerHTML : '';
      if (messageBox) {
        messageBox.innerHTML = emptySrc ? emptySrc.innerHTML : '';
        messageBox.hidden = !emptySrc;
      }
      if (hint) hint.hidden = true;

      /* Ids are stamped here rather than in Liquid so aria-activedescendant can
         never point at a stale or duplicated id from an earlier response. */
      options = $all('[role="option"]', listbox);
      options.forEach(function (el, i) {
        el.id = 'GkSrchOpt-' + i;
        el.setAttribute('aria-selected', 'false');
        el.setAttribute('tabindex', '-1');
      });

      setExpanded(options.length > 0);
      announce(count > 0 ? count + ' ' + wordText : noneText);
    }

    function search(q) {
      /* Retire the in-flight request FIRST — including on a cache hit. Otherwise
         typing "ore" → "oreg" → back to "ore" would serve the cached "ore" render
         and then let the still-running "oreg" response overwrite it. */
      if (controller) { controller.abort(); controller = null; }
      var mine = ++token;

      var key = q.toLowerCase();
      if (cache[key]) { apply(cache[key]); return; }

      controller = ('AbortController' in window) ? new AbortController() : null;
      root.classList.add('is-loading');

      var url = suggestUrl +
        (suggestUrl.indexOf('?') > -1 ? '&' : '?') +
        'q=' + encodeURIComponent(q) +
        '&resources[type]=' + encodeURIComponent(types) +
        '&resources[limit]=' + limit +
        '&resources[options][unavailable_products]=last' +
        '&section_id=predictive-search';

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) {
          if (!r.ok) throw new Error('predictive search failed');
          return r.text();
        })
        .then(function (html) {
          if (mine !== token) return; // a newer keystroke already won
          cache[key] = html;
          apply(html);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (mine !== token) return;
          // Honest failure: no results are invented, the real search page still works.
          showMessage(errorText);
        });
    }

    Panel({
      panel: root,
      openers: toggles,
      closers: $all('[data-search-close]', root),
      overlay: $('[data-search-overlay]', root),
      onOpen: function () {
        var q = input.value.trim();
        if (q.length < minChars) {
          reset(true);
        } else {
          setExpanded(options.length > 0);
        }
        if (clearBtn) clearBtn.hidden = input.value.length === 0;
        if (q) { try { input.select(); } catch (e) { /* selection is cosmetic */ } }
      }
    });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      window.clearTimeout(timer);
      if (clearBtn) clearBtn.hidden = input.value.length === 0;

      if (q.length < minChars) {
        token++; // invalidate anything still in flight
        if (controller) { controller.abort(); controller = null; }
        reset(true);
        return;
      }
      timer = window.setTimeout(function () { search(q); }, 220);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        if (!options.length) return;
        e.preventDefault();
        setActive(activeIndex + 1);
      } else if (e.key === 'ArrowUp') {
        if (!options.length) return;
        e.preventDefault();
        setActive(activeIndex - 1);
      } else if (e.key === 'Home' && activeIndex > -1) {
        e.preventDefault();
        setActive(0);
      } else if (e.key === 'End' && activeIndex > -1) {
        e.preventDefault();
        setActive(options.length - 1);
      } else if (e.key === 'Enter') {
        var el = options[activeIndex];
        if (!el) return; // no highlight: let the form submit to the real search page
        e.preventDefault();
        var href = el.getAttribute('href');
        if (href) window.location.href = href;
      }
      // Escape is handled by Panel — it closes the whole overlay.
    });

    /* Never show a hover highlight and a keyboard highlight at once. */
    listbox.addEventListener('pointerover', function (e) {
      if (activeIndex < 0 || !e.target.closest) return;
      var opt = e.target.closest('[role="option"]');
      if (opt && opt !== options[activeIndex]) clearActive();
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        window.clearTimeout(timer);
        token++;
        if (controller) { controller.abort(); controller = null; }
        input.value = '';
        clearBtn.hidden = true;
        reset(true);
        input.focus();
      });
    }
  }

  /* ---------- announcement rotator ---------- */
  var announcementRotators = [];
  var announcementVisibilityBound = false;

  function initAnnouncement(scope) {
    $all('[data-announcement]', scope || document).forEach(function (bar) {
      if (bar.dataset.announcementReady === 'true') return;
      var items = $all('[data-announcement-item]', bar);
      if (items.length < 2 || reduceMotion.matches) return;
      bar.dataset.announcementReady = 'true';

      var delay = (parseFloat(bar.getAttribute('data-rotate')) || 5) * 1000;
      var index = 0;
      var timer = null;

      function show(i) {
        items.forEach(function (item, n) {
          var active = n === i;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      }
      function next() {
        index = (index + 1) % items.length;
        show(index);
      }
      function start() { stop(); timer = window.setInterval(next, delay); }
      function stop() { window.clearInterval(timer); }

      show(0);
      start();
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      announcementRotators.push({ start: start, stop: stop });
    });

    /* Bind visibility handling once for all bars (avoids editor listener leak) */
    if (!announcementVisibilityBound) {
      announcementVisibilityBound = true;
      document.addEventListener('visibilitychange', function () {
        announcementRotators.forEach(function (r) {
          if (document.hidden) { r.stop(); } else { r.start(); }
        });
      });
    }
  }

  /* ---------- buy section: qty stepper + ajax add-to-cart ---------- */
  function initBuy() {
    $all('.geckio-buy__form').forEach(function (form) {
      if (form.dataset.buyReady === 'true') return;
      form.dataset.buyReady = 'true';

      function getQty() {
        var checked = form.querySelector('[name="quantity"]:checked');
        return Math.max(1, parseInt(checked && checked.value, 10) || 1);
      }

      form.addEventListener('submit', function (e) {
        var mainInput = form.querySelector('[name="id"]');
        if (!mainInput || !mainInput.value) return; // no JS-handled variant — let native form post
        e.preventDefault();
        if (form.dataset.submitting === 'true') return; // guard against double-add
        form.dataset.submitting = 'true';
        var items = [{ id: mainInput.value, quantity: getQty() }];
        var addon = form.querySelector('[data-addon-variant]');
        if (addon && addon.checked) items.push({ id: addon.getAttribute('data-addon-variant'), quantity: 1 });
        var btn = form.querySelector('[type="submit"]');
        if (btn) btn.setAttribute('aria-disabled', 'true');
        addToCart(items, function (err) {
          form.dataset.submitting = 'false';
          if (btn) btn.removeAttribute('aria-disabled');
          if (err) {
            var el = form.querySelector('[data-buy-error]');
            if (el) el.hidden = false;
          }
        });
      });
    });
  }

  /* ---------- cart: section-render refresh + drawer ---------- */
  var cartDrawerApi = null;
  var cartDrawerPanel = null;

  function applyCartSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var doc = new DOMParser().parseFromString(sections[id], 'text/html');
      if (id === 'cart-drawer') {
        var incoming = doc.querySelector('[data-cart-contents]');
        var current = $('[data-cart-contents]');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
        var newTitle = doc.querySelector('[data-cart-title-count]');
        var curTitle = $('[data-cart-title-count]');
        if (newTitle && curTitle) curTitle.textContent = newTitle.textContent;
      } else {
        // header (or any section holding the cart count badge)
        var newCount = doc.querySelector('[data-cart-count]');
        $all('[data-cart-count]').forEach(function (curCount) {
          if (!newCount) return;
          curCount.textContent = newCount.textContent;
          curCount.classList.toggle('is-empty', newCount.classList.contains('is-empty'));
        });
      }
    });
    // The drawer just re-rendered from the server: reconcile the automated lines
    // against the fresh, real state, and top up the recommended upsells.
    scheduleCartFx();
    initCartRecs();
      initCartTerms();
  }

  /* ---------- cart automations: reward checkpoints ----------
     The ONLY source of truth is the [data-cart-reward] nodes, which the
     cart-drawer section renders server-side from the real cart — one per
     checkpoint whose reward is "add a product automatically". This module never
     decides on its own whether a shopper qualifies; it just moves the cart
     towards what the server already said, ONE request at a time, then stops.

     Loop guards, in order:
     1. `busy` — one automated request at a time, ever.
     2. Every reward line carries the _geckio_reward line property (its value is
        the checkpoint's block id), so the server can always tell "this reward is
        already here" and stops asking for another. Those lines are also excluded
        server-side from the total the thresholds are measured against, so adding
        or removing a reward can never move the ladder and trigger the next one.
     3. Declining a reward is remembered for the session, so a removal can never
        be answered with a fresh add.
     4. A hard attempt cap that only resets once the cart reaches a steady state
        (nothing left to do), so a misconfigured product can cost a few requests
        but never an infinite loop. */
  var cartFx = { busy: false, attempts: 0, timer: null };
  var CART_FX_MAX_ATTEMPTS = 8;

  function rewardDeclineKey(key) { return 'geckio:reward-declined:' + key; }
  function rewardDeclined(key) {
    if (!key) return false;
    try { return window.sessionStorage.getItem(rewardDeclineKey(key)) === '1'; } catch (e) { return false; }
  }
  function rewardDecline(key) {
    try { window.sessionStorage.setItem(rewardDeclineKey(key), '1'); } catch (e) { /* private mode: the reward simply returns */ }
  }
  function rewardUndecline(key) {
    try { window.sessionStorage.removeItem(rewardDeclineKey(key)); } catch (e) { /* nothing to clear */ }
  }

  function scheduleCartFx() {
    window.clearTimeout(cartFx.timer);
    cartFx.timer = window.setTimeout(runCartFx, 0);
  }

  function runCartFx() {
    var nodes = $all('[data-cart-reward]');
    if (!nodes.length) { cartFx.attempts = 0; return; }

    var todo = null;
    nodes.forEach(function (node) {
      var key = node.getAttribute('data-reward-key') || '';
      var variantId = node.getAttribute('data-reward-variant') || '';
      var qualified = node.getAttribute('data-reward-qualified') === 'true';
      var line = parseInt(node.getAttribute('data-reward-line'), 10) || 0;

      // Back below the threshold: the shopper can earn this reward again later.
      if (!qualified && key) rewardUndecline(key);

      if (todo) return; // one reconciliation per pass — the re-render schedules the next
      if (qualified && !line && key && variantId && !rewardDeclined(key)) {
        todo = { add: true, key: key, variant: variantId };
      } else if (!qualified && line) {
        // Below its threshold again: take the reward back out. The removal cannot
        // re-trigger an add, because reward lines never counted towards the total.
        todo = { add: false, line: line };
      }
    });

    if (cartFx.busy) return;

    // Steady state — the cart matches what the server asked for.
    if (!todo) { cartFx.attempts = 0; return; }
    if (cartFx.attempts >= CART_FX_MAX_ATTEMPTS) return;

    cartFx.attempts++;
    cartFx.busy = true;
    if (todo.add) {
      addToCart(
        [{ id: todo.variant, quantity: 1, properties: { _geckio_reward: todo.key } }],
        function () { cartFx.busy = false; },
        { silent: true }
      );
    } else {
      changeCart(todo.line, 0, function () { cartFx.busy = false; });
    }
  }

  /* ---------- cart drawer: Shopify's own recommendations ----------
     The drawer re-renders itself through the product-recommendations route, so
     the injected rows are real products with real, money-formatted prices. Only
     the finished markup travels — nothing is priced or invented in the browser. */
  var cartRecsCache = {};
  var cartRecsPending = {};

  /* Rows are filtered against the cart server-side when they are fetched, but a
     cached list is reused across cart changes — so drop anything the shopper has
     since added, and blank the list entirely when nothing is left (the section's
     :empty rule then hides the heading too). */
  function pruneCartRecs(list, inCart) {
    if (inCart) {
      $all('[data-cart-add]', list).forEach(function (btn) {
        if (inCart.indexOf('|' + btn.getAttribute('data-cart-add') + '|') === -1) return;
        var row = btn.closest('.geckio-cart__upsell-row');
        if (row) row.parentNode.removeChild(row);
      });
    }
    if (!list.querySelector('.geckio-cart__upsell-row')) list.innerHTML = '';
  }
  /**
   * Shopify drops the recurring-billing consent copy into the additional-checkout-buttons
   * slot as plain body text. It is required wording, so it is never rewritten — only folded
   * into a <details> so it stops dominating the drawer. Elements containing a button, form
   * or iframe are skipped so Shopify's own express buttons are never touched.
   */
  function initCartTerms(root) {
    var scope = root || document;
    $all('[data-cart-express]', scope).forEach(function (box) {
      if (box.dataset.termsReady === '1') return;
      box.dataset.termsReady = '1';
      var label = box.getAttribute('data-terms-label') || (window.geckioStrings || {}).subscriptionTerms || 'Subscription terms';
      Array.prototype.slice.call(box.children).forEach(function (el) {
        if (el.tagName === 'DETAILS') return;
        if (el.querySelector('button, a, form, iframe, input, shopify-accelerated-checkout')) return;
        var text = (el.textContent || '').trim();
        if (text.length < 120) return;                 // short notices can stay as they are
        var d = document.createElement('details');
        d.className = 'geckio-cart__terms';
        var sm = document.createElement('summary');
        sm.textContent = label;
        var body = document.createElement('div');
        body.className = 'geckio-cart__terms-body';
        el.parentNode.insertBefore(d, el);
        d.appendChild(sm);
        d.appendChild(body);
        body.appendChild(el);
      });
    });
  }


  function initCartRecs() {
    var root = $('[data-cart-recs]');
    if (!root) return;
    var list = root.querySelector('[data-cart-recs-list]');
    if (!list || list.children.length) return; // already filled server-side
    var url = root.getAttribute('data-rec-url');
    if (!url) return;
    var inCart = root.getAttribute('data-in-cart') || '';
    if (cartRecsCache[url]) {
      list.innerHTML = cartRecsCache[url];
      pruneCartRecs(list, inCart);
      return;
    }
    if (cartRecsPending[url]) return;
    cartRecsPending[url] = true;

    fetch(url)
      .then(function (r) { if (!r.ok) throw new Error('cart recommendations failed'); return r.text(); })
      .then(function (html) {
        cartRecsPending[url] = false;
        var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-recs-list]');
        if (!incoming) return;
        cartRecsCache[url] = incoming.innerHTML;
        // The drawer may have re-rendered while the request was in flight.
        var live = $('[data-cart-recs-list]');
        if (!live || live.children.length) return;
        live.innerHTML = incoming.innerHTML;
        var liveRoot = live.closest('[data-cart-recs]');
        pruneCartRecs(live, liveRoot ? liveRoot.getAttribute('data-in-cart') || '' : '');
      })
      .catch(function () { cartRecsPending[url] = false; /* graceful: no upsells */ });
  }

  function cartSectionList() {
    return $('[data-drawer="cart"]') ? ['cart-drawer', 'header'] : ['header'];
  }

  /* opts.silent = update the cart + re-render, but do NOT open the drawer and do
     NOT bounce to /cart. The drawer's own automations (reward checkpoints, shipping
     protection) use it so the cart can heal itself without hijacking the page. */
  function addToCart(items, cb, opts) {
    var silent = !!(opts && opts.silent);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: cartSectionList(), sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function (data) {
        applyCartSections(data.sections);
        if (!silent) {
          if (cartDrawerApi) { cartDrawerApi.open(); } else { window.location.href = '/cart'; }
        }
        if (cb) cb(null, data);
      })
      .catch(function (e) { if (cb) cb(e); });
  }

  function changeCart(line, qty, cb) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty, sections: cartSectionList(), sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then(function (data) { applyCartSections(data.sections); if (cb) cb(null, data); })
      .catch(function (e) { if (cb) cb(e); });
  }

  function updateCartNote(note) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: note })
    }).catch(function () {});
  }

  function initCart() {
    var panel = $('[data-drawer="cart"]');
    if (!panel) return;
    /* Re-point at the CURRENT panel. The theme editor replaces this node whenever
       a cart-drawer setting changes, and a Panel built around the old (detached)
       node would silently open nothing — which would make every setting below
       impossible to preview. */
    if (!cartDrawerApi || cartDrawerPanel !== panel) {
      cartDrawerApi = Panel({
        panel: panel,
        openers: $all('[data-drawer-open="cart"]'),
        closers: $all('[data-drawer-close="cart"]'),
        overlay: $('[data-drawer-overlay="cart"]')
      });
      cartDrawerPanel = panel;
    }
    if (panel.dataset.cartBound === 'true') return;
    panel.dataset.cartBound = 'true';
    // Event delegation so the bound handlers survive section re-renders.
    panel.addEventListener('click', function (e) {
      var closeBtn = e.target.closest('[data-drawer-close="cart"]');
      if (closeBtn) {
        e.preventDefault();
        if (cartDrawerApi) cartDrawerApi.close();
        return;
      }
      // Declining a reward is a deliberate choice: remember it BEFORE the line is
      // removed, so the reconciler cannot answer the removal with a new add.
      var decline = e.target.closest('[data-reward-decline]');
      if (decline) {
        e.preventDefault();
        if (decline.getAttribute('aria-disabled') === 'true') return;
        decline.setAttribute('aria-disabled', 'true');
        var declinedKey = decline.getAttribute('data-reward-key');
        if (declinedKey) rewardDecline(declinedKey);
        cartFx.busy = true;
        changeCart(parseInt(decline.getAttribute('data-line'), 10), 0, function () {
          cartFx.busy = false;
          decline.removeAttribute('aria-disabled');
        });
        return;
      }
      var change = e.target.closest('[data-cart-change]');
      if (change) {
        e.preventDefault();
        changeCart(parseInt(change.getAttribute('data-line'), 10), parseInt(change.getAttribute('data-qty'), 10));
        return;
      }
      var add = e.target.closest('[data-cart-add]');
      if (add) {
        e.preventDefault();
        if (add.getAttribute('aria-disabled') === 'true') return;
        add.setAttribute('aria-disabled', 'true');
        addToCart([{ id: add.getAttribute('data-cart-add'), quantity: 1 }], function (err) {
          // On success the whole drawer is replaced; on failure the button must
          // become clickable again rather than dying silently.
          if (err) add.removeAttribute('aria-disabled');
        });
      }
    });
    panel.addEventListener('change', function (e) {
      // Persist the optional order note (only present when the cart-note setting is on).
      var note = e.target.closest('[data-cart-note]');
      if (note) { updateCartNote(note.value); return; }

      /* Shipping protection. The checkbox is a plain opt-in: it adds or removes
         exactly one marked line. It shares cartFx.busy with the reward reconciler
         so the two automations can never post at the same time, and the marked
         line is excluded from the checkpoint totals server-side, so ticking it
         can never unlock a reward. */
      var prot = e.target.closest('[data-cart-protection]');
      if (!prot) return;
      var protVariant = prot.getAttribute('data-variant');
      var protLine = parseInt(prot.getAttribute('data-line'), 10) || 0;
      if (!protVariant) return;
      if (cartFx.busy) { prot.checked = !prot.checked; return; }

      var release = function (err) {
        cartFx.busy = false;
        prot.disabled = false;
        // The request failed, so the cart never changed: put the box back where
        // it was rather than showing a protection the shopper is not buying.
        if (err) prot.checked = !prot.checked;
      };

      if (prot.checked && !protLine) {
        cartFx.busy = true;
        prot.disabled = true;
        addToCart(
          [{ id: protVariant, quantity: 1, properties: { _geckio_protection: 'true' } }],
          release,
          { silent: true }
        );
      } else if (!prot.checked && protLine) {
        cartFx.busy = true;
        prot.disabled = true;
        changeCart(protLine, 0, release);
      }
    });

    /* Discount code. Shopify's AJAX cart cannot apply a code, so this is a REAL
       request, never a faked "applied" state: the form navigates to Shopify's own
       /discount/CODE link with a redirect back to the current page, which is the
       one mechanism that reliably puts the code on the cart and carries it into
       checkout. Without JS the same form posts the code to /cart. */
    panel.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-cart-discount-form]');
      if (!form) return; // the checkout form posts normally
      var input = form.querySelector('input[name="discount"]');
      var code = input && input.value ? input.value.trim() : '';
      e.preventDefault();
      if (!code) { if (input) input.focus(); return; }
      var btn = form.querySelector('[type="submit"]');
      if (btn) btn.setAttribute('aria-disabled', 'true');
      var base = (form.getAttribute('data-root') || '/').replace(/\/$/, '');
      var back = window.location.pathname + window.location.search;
      window.location.href = base + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(back);
    });

    // First paint: the drawer may already be rendered above/below a threshold.
    scheduleCartFx();
    initCartRecs();
      initCartTerms();
  }

  /* ---------- sticky add-to-cart bar (product page) ---------- */
  function initStickyAtc() {
    $all('[data-sticky-atc]').forEach(function (bar) {
      if (bar.dataset.stickyReady === 'true') return;
      bar.dataset.stickyReady = 'true';
      var anchor = $('[data-atc-anchor]');
      var form = $('.geckio-buy__form');
      var trigger = bar.querySelector('[data-sticky-atc-btn]');
      if (trigger && form) {
        trigger.addEventListener('click', function () {
          if (form.requestSubmit) { form.requestSubmit(); }
          else { form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })); }
        });
      }
      if (!anchor || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { bar.classList.toggle('is-visible', !e.isIntersecting); });
      }, { rootMargin: '0px 0px -20% 0px' });
      io.observe(anchor);
    });
  }

  /* ---------- money, learned from Shopify's own output ----------
     A quantity tier is priced "variant price x quantity" at render time. The moment the
     shopper picks a variant that costs something else, that tier shows a number the cart will
     not honour — so it has to be recomputed, which means multiplying cents and printing them.
     A storefront has no money formatter, and Intl would invent its own layout ("kr 597,00"
     next to the shop's "597,00 kr" reads as broken).

     So learn the shop's format instead of guessing it: every variant ships both its formatted
     price and its raw cents, and that pair gives away prefix, suffix, decimals and separators.
     If the pair cannot be read this returns null and every price stays exactly as the server
     rendered it — stale is bad, invented is worse. */
  function learnMoney(list, extras) {
    var sample = null, i;
    for (i = 0; i < list.length; i++) {
      if (typeof list[i].price === 'string' && typeof list[i].price_cents === 'number' && list[i].price_cents > 0) {
        sample = list[i];
        break;
      }
    }
    if (!sample) return null;

    var text = sample.price;
    // Digits, plus every separator a money format may put between them: period, comma,
    // apostrophe (CHF 1'299.00), and space / no-break space / narrow no-break space.
    var run = text.match(/\d[\d.,'\u2019\s\u00a0\u202f]*\d|\d/);
    if (!run) return null;
    var num = run[0];
    var fmt = {
      prefix: text.slice(0, run.index),
      suffix: text.slice(run.index + num.length),
      decimal: '',
      thousands: '',
      decimals: 0
    };

    // Does the printed number carry the cents, or has the shop turned decimals off?
    var digits = num.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    var cents = Math.round(sample.price_cents);
    if (digits === String(cents)) fmt.decimals = 2;
    else if (digits === String(Math.round(cents / 100))) fmt.decimals = 0;
    else return null;

    if (fmt.decimals === 2) {
      var dsep = num.charAt(num.length - 3);
      if (!dsep || /\d/.test(dsep)) return null;
      fmt.decimal = dsep;
    }

    /* Grouping only shows itself above a thousand and plenty of catalogues never get there,
       so read every money string on the section before falling back to the counterpart of
       the decimal separator (the two formats that cover almost every shop: 1.299,00 / 1,299.00). */
    var pool = [];
    for (i = 0; i < list.length; i++) if (typeof list[i].price === 'string') pool.push(list[i].price);
    if (extras) for (i = 0; i < extras.length; i++) if (extras[i]) pool.push(String(extras[i]));
    for (i = 0; i < pool.length; i++) {
      var grp = pool[i].match(/\d{1,3}(\D)\d{3}(?!\d)/);
      if (grp && grp[1] !== fmt.decimal) { fmt.thousands = grp[1]; break; }
    }
    if (!fmt.thousands) {
      if (fmt.decimal === ',') fmt.thousands = '.';
      else if (fmt.decimal === '.') fmt.thousands = ',';
    }
    return fmt;
  }

  function formatMoney(fmt, cents) {
    if (!fmt || typeof cents !== 'number' || !isFinite(cents)) return null;
    var v = Math.round(Math.abs(cents));
    var whole = String(fmt.decimals === 2 ? Math.floor(v / 100) : Math.round(v / 100));
    if (fmt.thousands) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousands);
    var out = whole;
    if (fmt.decimals === 2) {
      var frac = String(v % 100);
      if (frac.length < 2) frac = '0' + frac;
      out += fmt.decimal + frac;
    }
    return fmt.prefix + out + fmt.suffix;
  }

  /* ---------- shared variant matching ----------
     Every picker in the theme is the same problem: read the chosen option values off a set of
     radios or selects, then find the variant that carries them. An option nobody has chosen
     yet is left undefined and simply does not narrow the search. */
  function optionSelection(controls) {
    var want = [];
    controls.forEach(function (c) {
      var pos = parseInt(c.getAttribute('data-option-position'), 10);
      if (isNaN(pos)) return;
      if (c.tagName === 'SELECT') want[pos] = c.value;
      else if (c.checked) want[pos] = c.value;
    });
    return want;
  }

  function variantMatching(list, want) {
    for (var i = 0; i < list.length; i++) {
      var opts = list[i].options || [];
      var hit = true;
      for (var n = 0; n < want.length; n++) {
        if (want[n] === undefined) continue;
        if (opts[n] !== want[n]) { hit = false; break; }
      }
      if (hit) return list[i];
    }
    return null;
  }

  /* The shared picker prints the chosen value beside the option name ("Farve: Sand"), and
     nothing in the theme has ever updated it — so it kept announcing the variant the page
     opened on while the swatches underneath said something else. The label belongs to the
     picker its control sits in (closest), which is what keeps the three pickers of a
     per-unit bundle from overwriting each other's. */
  function paintChosenValue(control) {
    var scope = control.closest ? control.closest('[data-geckio-vp]') : null;
    if (!scope) return;
    var pos = control.getAttribute('data-option-position');
    if (pos === null) return;
    var out = scope.querySelector('[data-geckio-vp-chosen="' + pos + '"]');
    if (out) out.textContent = control.value;
  }

  function findVariantById(list, id) {
    for (var i = 0; i < list.length; i++) if (String(list[i].id) === String(id)) return list[i];
    return null;
  }

  /* One cart line per variant, quantities summed. Pushing the same variant twice makes the
     drawer show it twice; and a subscription line may never be merged into a one-time one —
     different price, different commitment. */
  function mergeLine(lines, id, quantity, plan) {
    if (!id || !(quantity > 0)) return;
    for (var i = 0; i < lines.length; i++) {
      if (String(lines[i].id) === String(id) && (lines[i].selling_plan || '') === (plan || '')) {
        lines[i].quantity += quantity;
        return;
      }
    }
    var line = { id: id, quantity: quantity };
    if (plan) line.selling_plan = plan;
    lines.push(line);
  }

  /* ---------- Geckio PDP: bundles + gallery + add-to-cart ---------- */
  function initGeckioProduct() {
    $all('[data-geckio-product]').forEach(function (root) {
      if (root.dataset.hpReady === 'true') return;
      root.dataset.hpReady = 'true';

      var form = root.querySelector('[data-hp-form]');
      var qtyInput = root.querySelector('[data-hp-qty]');
      /* The visible stepper is a control, not the payload — it writes into the hidden
         quantity field the form and the AJAX submit already read, so the bundle flow is
         untouched whether the stepper is switched on or not. */
      var qtyStepper = root.querySelector('[data-hp-qty-input]');
      if (qtyStepper && qtyInput) {
        var syncQty = function () {
          var n = parseInt(qtyStepper.value, 10);
          if (!isFinite(n) || n < 1) n = 1;
          qtyStepper.value = n;
          qtyInput.value = n;
        };
        var stepQty = function (delta) {
          qtyStepper.value = (parseInt(qtyStepper.value, 10) || 1) + delta;
          syncQty();
        };
        var qtyDown = root.querySelector('[data-hp-qty-down]');
        var qtyUp = root.querySelector('[data-hp-qty-up]');
        if (qtyDown) qtyDown.addEventListener('click', function () { stepQty(-1); });
        if (qtyUp) qtyUp.addEventListener('click', function () { stepQty(1); });
        qtyStepper.addEventListener('change', syncQty);
        syncQty();
      }

      var addonInput = root.querySelector('[data-hp-addon]');
      var addonColors = root.querySelector('[data-hp-addon-colors]');
      var addonColorRadios = $all('[data-hp-addon-color]', root);
      var addonImg = root.querySelector('[data-hp-addon-img]');
      var addonSlides = $all('[data-hp-addon-slide]', root);
      function showAddonImage(vid) {
        if (!addonImg) return;
        addonImg.hidden = false;
        var matched = false;
        addonSlides.forEach(function (s) {
          var on = s.getAttribute('data-variant-id') === vid;
          s.classList.toggle('is-active', on);
          if (on) matched = true;
        });
        if (!matched && addonSlides.length) addonSlides[0].classList.add('is-active');
      }
      function hideAddonImage() { if (addonImg) addonImg.hidden = true; }
      function syncAddonColor(radio) {
        if (!radio) return;
        if (addonInput) addonInput.value = radio.value;
        addonColorRadios.forEach(function (r) {
          var lbl = r.closest('.addoncolor__opt');
          if (lbl) lbl.classList.toggle('sel', r === radio);
        });
        if (addonImg && !addonImg.hidden) showAddonImage(radio.value);
      }
      addonColorRadios.forEach(function (r) {
        r.addEventListener('change', function () { syncAddonColor(r); });
      });
      (function () {
        var c = null;
        addonColorRadios.forEach(function (r) { if (r.checked && !c) c = r; });
        if (!c && addonColorRadios.length) c = addonColorRadios[0];
        if (c) syncAddonColor(c);
      })();
      var nowEl = root.querySelector('[data-hp-now]');
      var wasEl = root.querySelector('[data-hp-was]');
      var saveEl = root.querySelector('[data-hp-save]');
      var stickyNowEl = root.querySelector('[data-hp-sticky-now]');
      var bundles = $all('[data-hp-bundle]', root);
      var selected = bundles[0] || null;

      /* ---------- variant picking inside the bundle, and extra products ----------
         All of it is set up here because selectBundle() below drives it and runs once on load.

         [data-hp-bundle-vp] is the picker that lives INSIDE a tier's own card: "single" asks
         once for the whole pack (a 3-pack, all the same size), "each" asks once per unit so
         three shirts can be three colours. There is one of these PER TIER, holding exactly as
         many units as that tier buys, and only the selected tier's is on screen.

         The EXTRA product belongs to the tier as well — a row is a deal, and "PerfectPillow +
         Betræk" is one of them. Its chosen id lives in the tier's own card, so a tier nobody
         selected can never contribute a cart line; its picker lives in a single row under the
         whole ladder ([data-hp-extra]), because that is where the shopper looks for it. The
         extra is its own cart line at its own price: the discount is a Shopify discount, not
         something the theme is allowed to invent. */
      var bundleVpRecs = [];
      $all('[data-hp-bundle-vp]', root).forEach(function (vp) {
        var rec = {
          el: vp,
          tier: vp.closest ? vp.closest('[data-hp-bundle]') : null,
          units: $all('[data-hp-bundle-unit]', vp),
          controls: {}
        };
        $all('[data-hp-bundle-variant]', vp).forEach(function (c) {
          // "single" mode has no data-unit at all: one unit, and it is unit 1.
          var n = parseInt(c.getAttribute('data-unit'), 10);
          if (isNaN(n)) {
            var wrap = c.closest('[data-hp-bundle-unit]');
            n = wrap ? parseInt(wrap.getAttribute('data-hp-bundle-unit'), 10) : 1;
          }
          if (isNaN(n) || n < 1) n = 1;
          if (!rec.controls[n]) rec.controls[n] = [];
          rec.controls[n].push(c);
        });
        bundleVpRecs.push(rec);
      });
      /* Kept as the "is there a picker inside the bundle at all" flag every branch below still
         asks, and as where the shared data-choice is read from. It is never used to READ a
         choice — that always goes through the selected tier. */
      var bundleVp = bundleVpRecs.length ? bundleVpRecs[0].el : null;
      var bundleChoice = bundleVp ? (bundleVp.getAttribute('data-choice') || 'single') : 'off';
      var bundleVariants = [];
      if (bundleVp) {
        /* One table for the whole ladder, sitting beside it rather than inside a tier.
           Written by Liquid from the real product, so id, price and availability come from
           Shopify. A picker that infers any of the three eventually sells something that is
           not there. */
        var bvDataEl = root.querySelector('[data-hp-bundle-variant-data]');
        if (bvDataEl) { try { bundleVariants = JSON.parse(bvDataEl.textContent) || []; } catch (e) { bundleVariants = []; } }
      }

      /* The picker that belongs to the tier currently on screen — the ONLY one anything is
         allowed to read. A hidden tier still holds a perfectly valid answer, for a pack the
         shopper is not buying, and reading it is how a cart line gets the wrong size. */
      function bundleVpRec() {
        if (!selected) return null;
        for (var i = 0; i < bundleVpRecs.length; i++) {
          if (bundleVpRecs[i].tier === selected) return bundleVpRecs[i];
        }
        return null;
      }

      /* One record per tier that HAS an extra — a tier without one gets none, so there is
         nothing about it that could reach the cart. The hidden id and the variant table are
         read out of the tier's own card; the picker is found afterwards in the row under the
         ladder and matched back by tier number. */
      var extraByTier = {};
      var extraRecs = [];
      bundles.forEach(function (b) {
        var input = b.querySelector('[data-hp-extra-variant]');
        if (!input) return;
        var radio = b.querySelector('.geckio-pdp__br');
        var n = radio ? parseInt(radio.value, 10) : 0;
        if (!(n > 0)) return;
        var rec = {
          tier: n,
          input: input,
          qty: Math.max(1, parseInt(b.getAttribute('data-extra-qty'), 10) || 1),
          variants: [],
          panel: null,
          controls: []
        };
        var exDataEl = b.querySelector('[data-hp-extra-data]');
        if (exDataEl) { try { rec.variants = JSON.parse(exDataEl.textContent) || []; } catch (e) { rec.variants = []; } }
        extraByTier[n] = rec;
        extraRecs.push(rec);
      });
      var extraRow = root.querySelector('[data-hp-extra]');
      $all('[data-hp-extra-panel]', root).forEach(function (panel) {
        var rec = extraByTier[parseInt(panel.getAttribute('data-hp-extra-panel'), 10)];
        if (!rec) return;
        rec.panel = panel;
        rec.controls = $all('[data-hp-extra-option]', panel);
      });

      var variantField = root.querySelector('[data-hp-variant]');

      /* The standalone Variant picker block lives in its own scope further down, so this is
         how the bundle reaches it. Two things went wrong without it:

         selectBundle() writes the tier's baked-in variant id into [data-hp-variant]. With the
         in-bundle picker off — the DEFAULT — nothing put the shopper's own choice back, so
         picking "white" and then clicking the 3-pack silently charged for the first colour.

         And availability was only ever checked when the in-bundle picker existed, so a
         sold-out variant chosen in the standalone picker sailed into the cart and failed at
         checkout, after the address. */
      var reapplyStandalonePick = null;
      var mainVariantData = [];
      (function () {
        var el = root.querySelector('[data-hp-variant-data]');
        if (!el) return;
        try { mainVariantData = JSON.parse(el.textContent) || []; } catch (e) { mainVariantData = []; }
      })();
      var ctaBtn = root.querySelector('[data-hp-atc]');
      /* The variant the server priced the tiers from — read BEFORE selectBundle() runs, because
         a subscription tier overwrites this field with its own id on load. */
      var baseVariant = findVariantById(bundleVariants, variantField ? variantField.value : '');
      var tierBase = bundles.map(function (b) {
        return {
          now: b.getAttribute('data-now') || '',
          was: b.getAttribute('data-was') || '',
          save: b.getAttribute('data-save') || ''
        };
      });
      var moneyFmt = bundleVariants.length ? learnMoney(bundleVariants, tierBase.map(function (t) { return t.now; })) : null;

      var errEl = root.querySelector('[data-hp-err]');
      var errAddMsg = errEl ? errEl.textContent : '';
      /* No new string for this: products.product.unavailable is already translated in every
         locale the theme ships, and "unavailable" is exactly what a sold-out pick is. */
      var errSoldMsg = (window.geckioStrings || {}).unavailable || errAddMsg;

      /* How many units the SELECTED tier buys. Its card renders exactly that many pickers, so
         this is really a sanity clamp: the tier's own quantity, never more units than it has
         controls for. */
      function bundleUnitCount() {
        if (!bundleVp) return 0;
        var rec = bundleVpRec();
        if (!rec) return 0;
        if (bundleChoice !== 'each') return 1;
        var q = selected ? parseInt(selected.getAttribute('data-qty'), 10) : 1;
        if (!q || q < 1) q = 1;
        if (q > rec.units.length) q = rec.units.length;
        return q;
      }

      /* The variant every unit of the selected tier resolves to. No other tier is consulted. */
      function bundleSelection() {
        var out = [];
        var rec = bundleVpRec();
        var need = bundleUnitCount();
        for (var n = 1; n <= need; n++) {
          var ctrls = (rec && rec.controls[n]) || [];
          out.push(ctrls.length ? variantMatching(bundleVariants, optionSelection(ctrls)) : baseVariant);
        }
        return out;
      }

      /* ---- carrying the shopper's pick from one tier to the next ----
         Each tier owns its own radios now, and they all start on the product's default. Without
         this, "black, then the 2-pack" would quietly go back to white — and, on a quantity tier,
         take the price with it. So the values move across on every tier change; a unit the
         previous tier never had inherits unit 1's answer. */
      function readUnitValues(rec, n) {
        var vals = {};
        var ctrls = (rec && rec.controls[n]) || [];
        ctrls.forEach(function (c) {
          var pos = c.getAttribute('data-option-position');
          if (pos === null) return;
          if (c.tagName === 'SELECT') vals[pos] = c.value;
          else if (c.checked) vals[pos] = c.value;
        });
        return vals;
      }

      function writeUnitValues(rec, n, vals) {
        var ctrls = (rec && rec.controls[n]) || [];
        ctrls.forEach(function (c) {
          var pos = c.getAttribute('data-option-position');
          if (pos === null || !Object.prototype.hasOwnProperty.call(vals, pos)) return;
          if (c.tagName === 'SELECT') {
            for (var i = 0; i < c.options.length; i++) {
              if (c.options[i].value === vals[pos]) {
                if (c.selectedIndex !== i) { c.selectedIndex = i; paintChosenValue(c); }
                break;
              }
            }
          } else if (c.value === vals[pos] && !c.checked) {
            c.checked = true;
            paintChosenValue(c);
          }
        });
      }

      function carryBundlePick(prev, next) {
        if (!bundleVp || !prev || !next || prev === next) return;
        var from = null, to = null, i;
        for (i = 0; i < bundleVpRecs.length; i++) {
          if (bundleVpRecs[i].tier === prev) from = bundleVpRecs[i];
          if (bundleVpRecs[i].tier === next) to = bundleVpRecs[i];
        }
        if (!from || !to) return;
        var units = to.units.length || 1;
        for (var n = 1; n <= units; n++) {
          var vals = readUnitValues(from, n);
          var empty = true;
          for (var k in vals) { if (Object.prototype.hasOwnProperty.call(vals, k)) { empty = false; break; } }
          if (empty) vals = readUnitValues(from, 1);
          writeUnitValues(to, n, vals);
        }
      }

      function paintPrice(b) {
        if (!b) return;
        if (nowEl && b.getAttribute('data-now')) nowEl.textContent = b.getAttribute('data-now');
        if (stickyNowEl && b.getAttribute('data-now')) stickyNowEl.textContent = b.getAttribute('data-now');
        if (wasEl) { var w = b.getAttribute('data-was'); if (w) { wasEl.textContent = w; wasEl.hidden = false; } else { wasEl.hidden = true; } }
        if (saveEl) { var s = b.getAttribute('data-save'); if (s) { saveEl.textContent = s; saveEl.hidden = false; } else { saveEl.hidden = true; } }
      }

      /* A quantity tier was priced "variant price x quantity" from the variant the page opened
         on. Pick a variant that costs something else and every one of those tier prices is a
         number the cart will not honour, so recompute them from the real cents. Only quantity
         tiers: a bundle tier's price is typed by the merchant, and a subscription tier's comes
         from the selling plan. Anything that cannot be formatted is left exactly as rendered. */
      function restateTierPrices(sel) {
        if (!bundleVp || !moneyFmt || !baseVariant) return;
        bundles.forEach(function (b, idx) {
          if (b.getAttribute('data-mode') !== 'antal') return;
          var q = parseInt(b.getAttribute('data-qty'), 10) || 1;
          var cents = 0;
          var known = true;
          for (var n = 0; n < q; n++) {
            // Tiers bigger than the current one preview with unit 1's pick — there is no
            // choice on record for a unit that is not on screen.
            var v = bundleChoice === 'each' ? (sel[n] || sel[0]) : sel[0];
            if (!v || typeof v.price_cents !== 'number') { known = false; break; }
            cents += v.price_cents;
          }
          var base = tierBase[idx];
          if (!known || cents === baseVariant.price_cents * q) {
            // Back on the variant the server priced: restore its exact strings, savings and all.
            b.setAttribute('data-now', base.now);
            b.setAttribute('data-was', base.was);
            b.setAttribute('data-save', base.save);
          } else {
            var txt = formatMoney(moneyFmt, cents);
            if (!txt) return;
            b.setAttribute('data-now', txt);
            /* was/save came from the compare-at of the variant the server priced, and that
               reaches us only as a formatted string — it cannot be multiplied for a different
               variant. A stale "was" beside a fresh "now" advertises a discount the shop is not
               giving, so both go until the shopper is back on the priced variant. */
            b.setAttribute('data-was', '');
            b.setAttribute('data-save', '');
          }
          var bp = b.querySelector('.bp');
          if (bp) bp.textContent = b.getAttribute('data-now');
          var bw = b.querySelector('.bw');
          if (bw) {
            var w = b.getAttribute('data-was');
            if (w) { bw.textContent = w; bw.hidden = false; } else { bw.hidden = true; }
          }
        });
        paintPrice(selected);
      }

      /* The hidden variant field is what the form posts with JS off and what the dynamic
         checkout button reads, so it carries unit 1's choice — the only single answer there is
         once "each" lets the units differ. The AJAX add below sends every unit. This also
         undoes the subscription tier's default-variant write in selectBundle(): the shopper
         chose a size, and a tier click may not silently replace it. */
      function applyBundleVariants() {
        if (!bundleVp) {
          /* No picker inside the bundle, but there may be a standalone one — and the tier
             click has just overwritten its answer. Put it back. */
          if (reapplyStandalonePick) reapplyStandalonePick();
          return;
        }
        var sel = bundleSelection();
        var first = sel.length ? sel[0] : null;
        if (first && variantField && String(variantField.value) !== String(first.id)) {
          variantField.value = first.id;
          /* Assigning .value fires nothing, so Shopify's payment-terms element and any app
             listening for a variant change never hear about it. */
          variantField.dispatchEvent(new Event('change', { bubbles: true }));
        }
        restateTierPrices(sel);
      }

      /* .geckio-pdp__br, not input[type="radio"]: the tier card now also contains the option
         radios of its own variant picker, and the first radio in the card is only the tier's
         when you ask for the tier's class. */
      function currentTier() {
        if (!selected) return 0;
        var r = selected.querySelector('.geckio-pdp__br');
        return r ? parseInt(r.value, 10) || 0 : 0;
      }

      /* The extra belonging to the deal the shopper is actually on — the only one anything is
         allowed to read or add. A tier they are not buying still holds a perfectly valid answer
         for a product they did not order. */
      function extraRec() {
        var tier = currentTier();
        return (tier > 0 && Object.prototype.hasOwnProperty.call(extraByTier, tier)) ? extraByTier[tier] : null;
      }

      /* ONE row under the ladder, showing the selected deal's extra. It appears, changes and
         disappears as the shopper moves between deals; a deal with no extra — or an extra that
         comes in exactly one version, which has nothing to ask — takes the whole row away. */
      function syncExtraRow() {
        if (!extraRecs.length) return;
        var cur = extraRec();
        var shown = false;
        for (var i = 0; i < extraRecs.length; i++) {
          var rec = extraRecs[i];
          if (!rec.panel) continue;
          var on = rec === cur;
          rec.panel.hidden = !on;
          if (on) shown = true;
        }
        if (extraRow) extraRow.hidden = !shown;
      }

      function applyExtraVariant(rec) {
        // No picker means one version only: the id the server wrote is the whole answer.
        if (!rec || !rec.controls.length) return;
        var v = variantMatching(rec.variants, optionSelection(rec.controls));
        // No match means the shopper has asked for a combination that does not exist. Blank the
        // field rather than leave the previous id behind — bundleReady() then blocks the add.
        rec.input.value = v ? v.id : '';
      }

      /* Everything the shopper is about to be charged for has to be buyable. Checking it here
         is the difference between "that colour is gone" on the product page and a checkout that
         refuses them after they have entered an address. */
      function bundleReady() {
        var i;
        if (bundleVp) {
          var sel = bundleSelection();
          for (i = 0; i < sel.length; i++) {
            if (!sel[i] || sel[i].available === false) return false;
          }
        } else if (variantField && variantField.value && mainVariantData.length) {
          /* The main product still has to be buyable when the bundle has no picker of its
             own — which is the default, and was the case this check skipped entirely. */
          var main = findVariantById(mainVariantData, variantField.value);
          if (main && main.available === false) return false;
        }
        /* The selected deal's extra has to be buyable too — a sold-out cover is the deal being
           unavailable, not something to discover after the address page. */
        var xrec = extraRec();
        if (xrec) {
          if (!xrec.input.value) return false;
          var xv = findVariantById(xrec.variants, xrec.input.value);
          if (xv && xv.available === false) return false;
        }
        return true;
      }

      /* A sold-out notice must not outlive the selection that caused it — only the message we
         put there is cleared, so a real add failure stays on screen. */
      function clearSoldOutNotice() {
        if (errEl && !errEl.hidden && errEl.textContent === errSoldMsg) errEl.hidden = true;
      }

      function updateBundleAtc() {
        // Untouched when the section has neither of the new features, so a page without them
        // behaves exactly as it did.
        if (!ctaBtn || (!bundleVp && !extraRecs.length)) return;
        /* The sticky bar carries its own copy of the button, and it is the one a shopper on a
           phone actually reaches — it follows them down the page while the real one is long
           scrolled past. Styling only the main button left the sticky one looking live with a
           sold-out cover selected, which is worse than not blocking at all: the shopper
           presses the button that looks fine and nothing happens. */
        var btns = [ctaBtn];
        var sticky = root.querySelector('[data-hp-sticky-atc]');
        if (sticky) btns.push(sticky);

        var ready = bundleReady();
        for (var b = 0; b < btns.length; b++) {
          if (ready) {
            btns[b].removeAttribute('aria-disabled');
            btns[b].removeAttribute('data-soldout');
          } else {
            btns[b].setAttribute('aria-disabled', 'true');
            btns[b].setAttribute('data-soldout', 'true');
          }
        }
        if (ready) clearSoldOutNotice();
      }

      function syncBundleState() {
        syncExtraRow();
        applyBundleVariants();
        updateBundleAtc();
      }

      function selectBundle(b) {
        var prev = selected;
        selected = b;
        /* 'sel' stays on the [data-hp-bundle] element — the card — because that is what the
           CSS styles as the chosen tier AND what shows its own variant picker. */
        bundles.forEach(function (x) {
          x.classList.remove('sel');
          var r = x.querySelector('.geckio-pdp__br');
          if (r) r.checked = false;
        });
        b.classList.add('sel');
        var radio = b.querySelector('.geckio-pdp__br');
        if (radio) radio.checked = true;
        if (qtyInput) qtyInput.value = b.getAttribute('data-qty') || '1';
        var bVid = b.getAttribute('data-variant-id');
        if (bVid) { var vInp = root.querySelector('[data-hp-variant]'); if (vInp) vInp.value = bVid;
          /* Assigning .value fires nothing, so Shopify's payment-terms element and any app
             listening for a variant change never hear about it. */
          vInp.dispatchEvent(new Event('change', { bubbles: true })); }
        // Subscription: a tier may carry a selling plan. Always WRITE the field
        // (empty = one-time) so switching from a plan tier back to a one-time tier
        // can never leave a stale plan id on the form.
        var planInput = root.querySelector('[data-hp-plan]');
        if (planInput) planInput.value = b.getAttribute('data-selling-plan') || '';
        paintPrice(b);
        var bAddon = b.getAttribute('data-addon') === 'true';
        if (addonColors) addonColors.hidden = !bAddon;
        if (bAddon) { showAddonImage(addonInput ? addonInput.value : null); } else { hideAddonImage(); }
        /* A different tier can mean a different number of units to choose for, a different set
           of extra products, and — because a quantity tier is priced per unit — different tier
           prices. It also has to put the shopper's own variant choice back after the variant
           write above, which a subscription tier makes with the default variant's id. */
        carryBundlePick(prev, b);
        syncBundleState();
      }
      bundles.forEach(function (b) {
        b.addEventListener('click', function (e) {
          /* The card is the click target for the tier, but it now contains the tier's own
             variant picker. A click on a swatch is a size choice, not a tier choice — letting
             it fall through would re-run selectBundle and rewrite the variant field. */
          var t = e.target;
          if (t && t.closest && t.closest('[data-hp-bundle-vp]')) return;
          selectBundle(b);
        });
      });
      // Sync form (variant id, qty, price) with the pre-selected bundle on load —
      // otherwise the hidden variant input keeps its server default and can post
      // the wrong variant (e.g. ?variant= URL, or sold-out first variant).
      if (selected) selectBundle(selected);

      /* One listener per control in every tier's picker. Only the selected tier's are reachable
         — the rest are display:none, so they are out of the tab order too — and any change
         re-resolves the whole buy box: the hidden variant field, the tier prices, and whether
         the button is allowed to add anything at all. */
      bundleVpRecs.forEach(function (rec) {
        $all('[data-hp-bundle-variant]', rec.el).forEach(function (c) {
          c.addEventListener('change', function () { paintChosenValue(c); syncBundleState(); });
        });
      });
      extraRecs.forEach(function (rec) {
        rec.controls.forEach(function (c) {
          c.addEventListener('change', function () { paintChosenValue(c); applyExtraVariant(rec); updateBundleAtc(); });
        });
        // Reconcile on load: the server checked the radios for the extra's default variant, but
        // a sold-out default can leave the hidden id disagreeing with them.
        applyExtraVariant(rec);
      });
      /* Belt and braces: selectBundle() already ran above whenever there is a ladder, and the
         extras only exist inside one — but nothing here may depend on that ordering. */
      if (bundleVp || extraRecs.length) syncBundleState();

      /* ---------- variant picker ----------
         Only present when there is NO bundle block: the Liquid refuses to render both,
         because the bundle tiers already write [data-hp-variant] and two controls owning
         one field is how a buyer pays for a size they did not choose.

         Without the block this section had no picker at all — the form carried a hidden id
         set to the first available variant and nothing could change it, so every buyer of a
         product with sizes got the first one. */
      var optionInputs = $all('[data-hp-option]', root);
      if (optionInputs.length) {
        var variantData = [];
        var dataEl = root.querySelector('[data-hp-variant-data]');
        /* Written by Liquid from the real product, so price and availability come from
           Shopify. A picker that infers either eventually sells something that is not there. */
        if (dataEl) { try { variantData = JSON.parse(dataEl.textContent) || []; } catch (e) { variantData = []; } }

        var variantInput = root.querySelector('[data-hp-variant]');
        var atcBtn = root.querySelector('[data-hp-atc]');
        var priceNow = root.querySelector('[data-hp-now]');

        function chosenOptions() {
          var out = [];
          for (var i = 0; i < optionInputs.length; i++) {
            var c = optionInputs[i];
            /* A <select> has no .checked — the property is undefined, so the old gate skipped
               every dropdown and the shopper's choice never reached the variant lookup. The
               picker snippet switches to a real <select> whenever the style asks for one AND
               unconditionally past twelve values, so a product with fourteen colours had a
               dropdown that moved nothing: price still, image still, hidden variant id still
               on whatever the page opened with. main-product's own reader has always had this
               branch; this one did not. */
            if (c.tagName !== 'SELECT' && !c.checked) continue;
            out[parseInt(c.getAttribute('data-option-position'), 10) || 0] = c.value;
          }
          return out;
        }

        function applyChoice() {
          var want = chosenOptions();
          var match = null;
          for (var v = 0; v < variantData.length; v++) {
            var opts = variantData[v].options || [];
            var same = opts.length === want.length;
            for (var o = 0; same && o < want.length; o++) if (opts[o] !== want[o]) same = false;
            if (same) { match = variantData[v]; break; }
          }
          if (!match) return;

          if (variantInput) {
            variantInput.value = match.id;
            /* Assigning .value fires nothing, so Shopify's payment-terms element and any
               app listening for a variant change never hear about it. */
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (priceNow && match.price) priceNow.textContent = match.price;

          /* A sold-out variant must stop the sale here rather than at checkout.
             Blocking is this picker's own business, but UNBLOCKING is not: the deal's extra
             product may be sold out too, and this picker cannot see it. It used to clear the
             block unconditionally whenever the main variant was fine, which quietly undid the
             bundle's own gate — pick a colour and the button lit up again with an unavailable
             cover still in the deal. So when there is anything else with a say, the central
             gate answers. */
          if (atcBtn) {
            if (match.available === false) {
              atcBtn.setAttribute('aria-disabled', 'true');
              atcBtn.setAttribute('data-soldout', 'true');
            } else if (bundleVp || extraRecs.length) {
              updateBundleAtc();
            } else {
              atcBtn.removeAttribute('aria-disabled');
              atcBtn.removeAttribute('data-soldout');
              clearSoldOutNotice();
            }
          }
        }

        for (var oi = 0; oi < optionInputs.length; oi++) {
          // Same picker snippet, same stale "Farve: Sand" label — kept in step here too, so a
          // section cannot show one picker that updates it and one that does not.
          optionInputs[oi].addEventListener('change', function () { paintChosenValue(this); applyChoice(); });
        }
        /* Hand the bundle a way back in. Clicking a tier writes that tier's baked-in variant
           id over whatever the shopper picked here, so the bundle calls this to restore the
           choice — otherwise "white, then the 3-pack" quietly ships the first colour. */
        reapplyStandalonePick = applyChoice;

        /* Run once on load: a ?variant= URL or a sold-out first variant means the server's
           hidden id and the checked radios can already disagree. */
        applyChoice();
      }

      var scroller = root.querySelector('[data-hp-mainscroll]');
      var slides = $all('[data-hp-slide]', root);
      var thumbs = $all('[data-hp-thumb]', root);
      function setActiveThumb(i) { thumbs.forEach(function (t, n) { t.classList.toggle('active', n === i); }); }
      // Desktop shows one slide via opacity (is-active); mobile keeps the scroll slider.
      function setActiveSlide(idx) {
        if (idx < 0 || idx >= slides.length) return;
        slides.forEach(function (s, n) { s.classList.toggle('is-active', n === idx); });
      }
      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          var idx = parseInt(thumb.getAttribute('data-index'), 10);
          if (isNaN(idx)) idx = i;
          setActiveSlide(idx);
          if (scroller && slides[idx]) scroller.scrollTo({ left: slides[idx].offsetLeft, behavior: 'smooth' });
          setActiveThumb(i);
        });
      });

      /* Bring a variant's own photo forward when it is chosen.
         Picking "white" and being left looking at the black pillow is the one moment a
         shopper is studying the picture hardest, so the gallery has to answer. Each slide
         carries data-variant-ids listing the variants that claim it; a general shot claims
         none, and a variant with no photo of its own leaves the gallery alone rather than
         jumping to an unrelated image. */
      function showVariantSlide(vid) {
        if (!vid || !slides.length) return;
        var want = String(vid);
        for (var i = 0; i < slides.length; i++) {
          var ids = (slides[i].getAttribute('data-variant-ids') || '').split(',');
          for (var k = 0; k < ids.length; k++) {
            if (ids[k] && ids[k] === want) {
              setActiveSlide(i);
              setActiveThumb(i);
              if (scroller) scroller.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
              return;
            }
          }
        }
      }

      /* One listener on the hidden field rather than one per control: the variant is written
         there by the standalone picker, by the bundle tiers and by the in-bundle picker, and
         every one of them dispatches change. Listening here means the gallery follows all
         three without any of them having to know it exists. */
      var vidField = root.querySelector('[data-hp-variant]');
      if (vidField) {
        vidField.addEventListener('change', function () { showVariantSlide(vidField.value); });
      }
      if (scroller && slides.length > 1) {
        var galRaf;
        scroller.addEventListener('scroll', function () {
          if (galRaf) window.cancelAnimationFrame(galRaf);
          galRaf = window.requestAnimationFrame(function () {
            var pos = scroller.scrollLeft, closest = 0, min = Infinity;
            slides.forEach(function (s, n) { var d = Math.abs(s.offsetLeft - pos); if (d < min) { min = d; closest = n; } });
            setActiveThumb(closest);
          });
        }, { passive: true });
      }

      $all('[data-hp-video]', root).forEach(function (v) {
        var pbtn = v.querySelector('[data-hp-play]');
        var video = v.querySelector('video');
        if (!pbtn || !video) return;
        pbtn.addEventListener('click', function () {
          video.setAttribute('controls', '');
          v.classList.add('is-playing');
          var pr = video.play();
          if (pr && pr.catch) pr.catch(function () {});
        });
      });

      if (form) {
        form.addEventListener('submit', function (e) {
          var variantInput = root.querySelector('[data-hp-variant]');
          if (!variantInput || !variantInput.value) return; // let native post happen
          e.preventDefault();
          if (form.dataset.submitting === 'true') return;

          /* Nothing sold out gets added. The shopper is told here, on the line that already
             exists under the button, instead of being handed a checkout that refuses them.
             data-soldout is set by the standalone picker as well, so both paths are covered. */
          var soldOut = !bundleReady() || (ctaBtn && ctaBtn.getAttribute('data-soldout') === 'true');
          if (soldOut) {
            if (errEl) { errEl.textContent = errSoldMsg; errEl.hidden = false; }
            return;
          }
          if (errEl) { errEl.textContent = errAddMsg; errEl.hidden = true; }

          form.dataset.submitting = 'true';
          var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
          // Subscription: the Ajax cart accepts selling_plan per item. Without this
          // the plan would post fine WITHOUT JS but be silently dropped WITH JS —
          // i.e. the customer is charged one-time while the UI said "abonnement".
          var planEl = root.querySelector('[data-hp-plan]');
          var plan = planEl && planEl.value ? planEl.value : '';
          var items = [];

          if (bundleVp && bundleChoice === 'each') {
            /* One line per unit the shopper chose for, identical picks collapsed into one line
               with a summed quantity. The unit count IS the tier's quantity, so it must not be
               multiplied by the hidden quantity field as well — that field was set to the same
               number by selectBundle(). A visible stepper is the shopper's own multiplier: two
               of a 3-pack is two of each pick. */
            var sel = bundleSelection();
            var mult = qtyStepper ? Math.max(1, parseInt(qtyStepper.value, 10) || 1) : 1;
            for (var u = 0; u < sel.length; u++) {
              if (sel[u]) mergeLine(items, sel[u].id, mult, plan);
            }
            if (!items.length) mergeLine(items, variantInput.value, qty, plan);
          } else {
            mergeLine(items, variantInput.value, qty, plan);
          }

          if (selected && selected.getAttribute('data-addon') === 'true' && addonInput && addonInput.value) {
            var addonQty = Math.max(1, parseInt(selected.getAttribute('data-addon-qty'), 10) || 1);
            // The addon is a DIFFERENT product — it must never inherit the plan.
            mergeLine(items, addonInput.value, addonQty, '');
          }

          /* The selected deal's extra product: its own line, at its own price, at the variant
             and quantity the deal asks for — and never the selling plan, because the extra is
             not the thing being subscribed to. mergeLine folds it into an existing line when it
             happens to be the same variant, so the cart shows one row with the sum.

             The visible stepper multiplies it too. Two of a "pillow + cover" deal is two of
             each; scaling only the pillow would have sent two pillows and one cover, at the
             price of two full deals. */
          var extraLine = extraRec();
          if (extraLine && extraLine.input.value) {
            var extraMult = qtyStepper ? Math.max(1, parseInt(qtyStepper.value, 10) || 1) : 1;
            mergeLine(items, extraLine.input.value, extraLine.qty * extraMult, '');
          }

          var btn = root.querySelector('[data-hp-atc]');
          if (btn) btn.setAttribute('aria-disabled', 'true');
          addToCart(items, function (err) {
            form.dataset.submitting = 'false';
            if (btn) btn.removeAttribute('aria-disabled');
            if (err && errEl) { errEl.textContent = errAddMsg; errEl.hidden = false; }
          });
        });
      }

      var shipEl = root.querySelector('[data-hp-ship]');
      if (shipEl) {
        var shipText = shipEl.querySelector('[data-hp-ship-text]');
        var cutoff = parseInt(shipEl.getAttribute('data-cutoff'), 10);
        if (isNaN(cutoff)) cutoff = 14;
        var cutoffMin = parseInt(shipEl.getAttribute('data-cutoff-min'), 10);
        if (isNaN(cutoffMin)) cutoffMin = 0;
        var cutoffSec = cutoff * 3600 + cutoffMin * 60;
        var shipTz = shipEl.getAttribute('data-tz') || 'Europe/Copenhagen';
        var tToday = shipEl.getAttribute('data-today') || (window.geckioStrings||{}).shipToday || 'bestil inden for %t, så sender vi i dag';
        var tDefault = shipEl.getAttribute('data-default') || (window.geckioStrings||{}).shipDefault || '1-3 hverdages levering';
        var shipFmt = null;
        try { shipFmt = new Intl.DateTimeFormat('en-GB', { timeZone: shipTz, weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }); } catch (e) {}
        var weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        var forceShip = /[?&]shippreview=1/i.test(window.location.search || '');
        function pad2(n) { return n < 10 ? '0' + n : '' + n; }
        function updateShip() {
          var wd, h, m, s;
          if (shipFmt) {
            var parts = {};
            shipFmt.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
            wd = parts.weekday; h = parseInt(parts.hour, 10) % 24; m = parseInt(parts.minute, 10); s = parseInt(parts.second, 10);
          } else {
            var d = new Date(); wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]; h = d.getHours(); m = d.getMinutes(); s = d.getSeconds();
          }
          var nowSec = h * 3600 + m * 60 + s;
          if (forceShip || (weekdays.indexOf(wd) !== -1 && nowSec < cutoffSec)) {
            var remain = cutoffSec - nowSec;
            if (remain <= 0) remain += 86400;
            var cd = pad2(Math.floor(remain / 3600)) + ':' + pad2(Math.floor((remain % 3600) / 60)) + ':' + pad2(remain % 60);
            shipText.innerHTML = tDefault + ' <span class="ship-sep">·</span> <span class="ship-today">' + tToday.replace('%t', '<strong>' + cd + '</strong>') + '</span>';
          } else {
            shipText.textContent = tDefault;
          }
        }
        updateShip();
        setInterval(updateShip, 1000);
      }

      var stickyBar = root.querySelector('[data-hp-sticky]');
      var mainCta = root.querySelector('[data-hp-atc]');
      if (stickyBar && mainCta && 'IntersectionObserver' in window) {
        var sio = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            stickyBar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
          });
        }, { threshold: 0 });
        sio.observe(mainCta);
      }
    });
  }

  /* ---------- testimonial marquee pause/play (WCAG 2.2.2) ---------- */
  function initMarquee() {
    $all('[data-marquee]').forEach(function (marquee) {
      if (marquee.dataset.marqueeReady === 'true') return;
      marquee.dataset.marqueeReady = 'true';
      var toggle = marquee.querySelector('[data-marquee-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        var paused = marquee.classList.toggle('is-paused');
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        var lbl = paused ? toggle.getAttribute('data-label-play') : toggle.getAttribute('data-label-pause');
        if (lbl) toggle.setAttribute('aria-label', lbl);
      });
    });
  }

  /* ---------- scroll reveal ---------- */
  function initReveal() {
    var nodes = $all('[data-reveal]');
    if (!nodes.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: [0, 0.1] });
    nodes.forEach(function (n) { if (!n.classList.contains('is-revealed')) io.observe(n); });
  }

  /* ---------- product gallery (buy section thumbnails) ---------- */
  function initGallery() {
    $all('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';
      var main = gallery.querySelector('.geckio-buy__media img');
      var thumbs = $all('[data-gallery-thumb]', gallery);
      if (!main || thumbs.length < 2) return;
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.getAttribute('data-full');
          if (full) { main.removeAttribute('srcset'); main.src = full; }
          thumbs.forEach(function (t) { t.removeAttribute('aria-current'); });
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  /* ---------- boot ---------- */
  function init() {
    initStickyHeader();
    syncHeaderHeight();
    initMenuDrawer();
    initPredictiveSearch();
    initCart();
    initAnnouncement();
    initBuy();
    initGallery();
    initStickyAtc();
    initGeckioProduct();
    initQuickView();
    initMegaMenu();
    initSizeGuide();
    initMarquee();
    initReveal();
    initMediaVideo();
    recordRecentlyViewed();
    initGeckioSections();
  }

  /* ---------- media videos: play only what is on screen ----------
     Every section renders through snippets/media.liquid, so this one observer covers
     all of them. The Liquid deliberately emits the <video> WITHOUT an autoplay
     attribute and marks it instead — three reasons:

       1. A browser honouring autoplay starts decoding immediately, even for a video
          five screens down. On a page with several of them that is the whole scroll
          budget spent before the visitor has seen anything.
       2. prefers-reduced-motion has to win. Autoplaying video is exactly the kind of
          motion that setting exists to stop, and an attribute in the markup cannot be
          taken back. Here we simply never call play(), and the poster stays — which is
          why the section keeps its image picker.
       3. Pausing off-screen gives laptops their battery back, which is the difference
          between a theme that feels fast and one that makes the fan spin. */
  function initMediaVideo() {
    var marks = document.querySelectorAll('[data-geckio-video][data-autoplay="true"]');
    if (!marks.length) return;

    var videos = [];
    for (var i = 0; i < marks.length; i++) {
      var box = marks[i].closest('.geckio-media');
      var vid = box && box.querySelector('video');
      if (vid) videos.push(vid);
    }
    if (!videos.length) return;

    /* Reduced motion: leave every poster showing, and give the visitor the controls so
       the video is still reachable — hidden, not removed. */
    if (reduceMotion.matches) {
      for (var r = 0; r < videos.length; r++) videos[r].setAttribute('controls', '');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      for (var f = 0; f < videos.length; f++) safePlay(videos[f]);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        var v = entries[e].target;
        if (entries[e].isIntersecting) safePlay(v);
        else if (!v.paused) v.pause();
      }
    }, { rootMargin: '200px 0px', threshold: 0.01 });

    for (var o = 0; o < videos.length; o++) io.observe(videos[o]);

    /* play() rejects when the browser blocks it — an unmuted file, a data-saver setting,
       a background tab. The rejection is a promise, so an unhandled one shows up in the
       merchant's console as an error they cannot act on. Muting and retrying once is the
       fix that actually works; after that we leave the poster alone. */
    function safePlay(v) {
      var p = v.play();
      if (!p || typeof p.catch !== 'function') return;
      p.catch(function () {
        v.muted = true;
        var again = v.play();
        if (again && typeof again.catch === 'function') again.catch(function () {});
      });
    }
  }

    /* ---------- quick view: product section fetched into a modal ---------- */
  function initQuickView() {
    var modal = $('[data-quick-view-modal]');
    if (!modal || modal.dataset.qvReady === 'true') return;
    modal.dataset.qvReady = 'true';

    var content = $('[data-quick-view-content]', modal);
    var status = $('[data-quick-view-status]', modal);
    var request = 0;

    var api = Panel({
      panel: modal,
      closers: $all('[data-quick-view-close]', modal),
      overlay: $('[data-quick-view-overlay]', modal)
    });

    function announce(text) { if (status) status.textContent = text; }

    function bindProduct(root) {
      var form = root.querySelector('.geckio-qv__form');
      var idInput = root.querySelector('[name="id"]');
      var qtyInput = root.querySelector('[data-qv-qty]');
      var priceEl = root.querySelector('[data-qv-price]');
      var compareEl = root.querySelector('[data-qv-compare]');
      var imgEl = root.querySelector('.geckio-qv__media img');
      var btn = root.querySelector('[data-qv-atc]');
      var options = $all('[data-qv-option]', root);
      var variants = [];
      var dataEl = root.querySelector('[data-qv-variants]');
      if (dataEl) { try { variants = JSON.parse(dataEl.textContent); } catch (err) { variants = []; } }

      function matchVariant() {
        var sel = [];
        options.forEach(function (input) {
          if (input.checked) sel[parseInt(input.getAttribute('data-option-index'), 10)] = input.value;
        });
        for (var i = 0; i < variants.length; i++) {
          var hit = true;
          for (var n = 0; n < sel.length; n++) {
            if (sel[n] !== undefined && variants[i].options[n] !== sel[n]) { hit = false; break; }
          }
          if (hit) return variants[i];
        }
        return null;
      }

      function applyVariant(v) {
        if (idInput) idInput.value = v ? v.id : '';
        if (priceEl && v) priceEl.textContent = v.price;
        if (compareEl) {
          if (v && v.compare_at) { compareEl.textContent = v.compare_at; compareEl.hidden = false; }
          else { compareEl.hidden = true; }
        }
        if (imgEl && v && v.image) { imgEl.removeAttribute('srcset'); imgEl.src = v.image; }
        if (btn) {
          if (v && v.available) {
            btn.removeAttribute('disabled');
            btn.textContent = btn.getAttribute('data-label-add');
          } else {
            btn.setAttribute('disabled', '');
            btn.textContent = btn.getAttribute('data-label-soldout');
          }
        }
      }

      options.forEach(function (input) {
        input.addEventListener('change', function () { applyVariant(matchVariant()); });
      });

      $all('[data-qv-step]', root).forEach(function (step) {
        step.addEventListener('click', function () {
          if (!qtyInput) return;
          var next = (parseInt(qtyInput.value, 10) || 1) + parseInt(step.getAttribute('data-qv-step'), 10);
          qtyInput.value = Math.max(1, next);
        });
      });

      if (form) {
        form.addEventListener('submit', function (e) {
          if (!idInput || !idInput.value) return; // no JS-handled variant — let native form post
          e.preventDefault();
          if (form.dataset.submitting === 'true') return; // guard against double-add
          form.dataset.submitting = 'true';
          var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
          if (btn) btn.setAttribute('aria-disabled', 'true');
          api.close(); // the cart drawer takes over on success
          addToCart([{ id: idInput.value, quantity: qty }], function (err) {
            form.dataset.submitting = 'false';
            if (btn) btn.removeAttribute('aria-disabled');
            if (err) {
              api.open();
              var el = root.querySelector('[data-qv-error]');
              if (el) el.hidden = false;
            }
          });
        });
      }
    }

    // Event delegation so triggers rendered later (pagination, re-renders) still work.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-view]');
      if (!trigger) return;
      var url = trigger.getAttribute('data-product-url');
      if (!url) return;
      e.preventDefault();
      var token = ++request;
      content.innerHTML = '';
      modal.classList.add('is-loading');
      announce((window.geckioStrings||{}).loading || 'Indlæser …');
      api.open();
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + sep + 'section_id=quick-view')
        .then(function (r) { if (!r.ok) throw new Error('quick view failed'); return r.text(); })
        .then(function (html) {
          if (token !== request) return;
          var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-view-product]');
          if (!incoming) throw new Error('quick view empty');
          modal.classList.remove('is-loading');
          content.innerHTML = '';
          content.appendChild(incoming);
          bindProduct(incoming);
          var title = incoming.querySelector('.geckio-qv__title');
          announce(title ? title.textContent : '');
        })
        .catch(function () {
          if (token !== request) return;
          window.location.href = url; // graceful fallback: the product page has it all
        });
    });
  }

/* ---------- header mega-menu (desktop dropdowns) ---------- */
  function initMegaMenu() {
    $all('[data-mega-nav]').forEach(function (nav) {
      if (nav.dataset.megaReady === 'true') return;
      var items = $all('[data-mega-item]', nav);
      if (!items.length) return;
      nav.dataset.megaReady = 'true';

      var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      var openItem = null;
      var closeTimer = null;

      function setState(item, on) {
        var toggle = $('[data-mega-toggle]', item);
        var panel = $('[data-mega-panel]', item);
        if (toggle) toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
        if (panel) panel.classList.toggle('is-open', on);
      }
      function close() {
        if (!openItem) return;
        setState(openItem, false);
        openItem = null;
      }
      function open(item) {
        if (openItem === item) return;
        close();
        openItem = item;
        setState(item, true);
      }

      items.forEach(function (item) {
        var toggle = $('[data-mega-toggle]', item);
        if (!toggle) return;

        toggle.addEventListener('click', function () {
          if (openItem === item) { close(); } else { open(item); }
        });

        // Hover open/close on true pointer devices; the delay bridges the
        // gap between the trigger and the panel below the header.
        item.addEventListener('mouseenter', function () {
          if (!canHover.matches) return;
          window.clearTimeout(closeTimer);
          open(item);
        });
        item.addEventListener('mouseleave', function () {
          if (!canHover.matches) return;
          window.clearTimeout(closeTimer);
          closeTimer = window.setTimeout(function () {
            if (openItem === item) close();
          }, 200);
        });

        // Keyboard: close when focus tabs out of the item + its panel.
        item.addEventListener('focusout', function (e) {
          if (openItem !== item) return;
          if (!e.relatedTarget || item.contains(e.relatedTarget)) return;
          close();
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !openItem) return;
        var toggle = $('[data-mega-toggle]', openItem);
        close();
        if (toggle) toggle.focus();
      });
      document.addEventListener('click', function (e) {
        if (openItem && !openItem.contains(e.target)) close();
      });
    });
  }

/* ---------- size guide modal (buy section) ---------- */
  function initSizeGuide() {
    var panel = $('[data-drawer="size-guide"]');
    if (!panel || panel.dataset.sizeGuideReady === 'true') return;
    panel.dataset.sizeGuideReady = 'true';
    Panel({
      panel: panel,
      openers: $all('[data-drawer-open="size-guide"]'),
      closers: $all('[data-drawer-close="size-guide"]'),
      overlay: $('[data-drawer-overlay="size-guide"]')
    });
  }

  /* Boot when the DOM is ready. theme.js is deferred, so readyState is
     normally 'interactive'/'complete' here and the else branch runs — which
     means every init function must live at this top level (NOT inside the
     if), or init() would call hoisted-but-undefined functions and throw,
     leaving all [data-reveal] content stuck at opacity:0. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  /* ===== Geckio section modules (14 nye sektioner) ===== */

/* [before-after] initBeforeAfter */
  /* ---------- before/after image comparison slider ---------- */
  function initBeforeAfter() {
    $all('[data-before-after]').forEach(function (frame) {
      if (frame.dataset.baReady === 'true') return;
      frame.dataset.baReady = 'true';

      var handle = frame.querySelector('[data-ba-handle]');
      var section = frame.closest('.geckio-ba');
      if (section) section.classList.add('is-ready');

      var STEP = 1, BIG = 10;
      var dragging = false;

      function clamp(v) { return Math.max(0, Math.min(100, v)); }
      function current() { return parseFloat(frame.style.getPropertyValue('--ba-pos')) || 50; }

      function setPos(v) {
        v = clamp(v);
        frame.style.setProperty('--ba-pos', v);
        if (handle) {
          var rounded = Math.round(v);
          handle.setAttribute('aria-valuenow', rounded);
          handle.setAttribute('aria-valuetext', rounded + '%');
        }
      }

      function posFromEvent(e) {
        var rect = frame.getBoundingClientRect();
        if (!rect.width) return;
        setPos((e.clientX - rect.left) / rect.width * 100);
      }

      frame.addEventListener('pointerdown', function (e) {
        dragging = true;
        frame.classList.add('is-dragging');
        if (frame.setPointerCapture) { try { frame.setPointerCapture(e.pointerId); } catch (err) {} }
        posFromEvent(e);
        if (handle) handle.focus();
      });
      frame.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        posFromEvent(e);
      });
      function endDrag() {
        dragging = false;
        frame.classList.remove('is-dragging');
      }
      frame.addEventListener('pointerup', endDrag);
      frame.addEventListener('pointercancel', endDrag);

      if (handle) {
        handle.addEventListener('keydown', function (e) {
          var pos = current(), handled = true;
          switch (e.key) {
            case 'ArrowLeft': case 'ArrowDown': setPos(pos - STEP); break;
            case 'ArrowRight': case 'ArrowUp': setPos(pos + STEP); break;
            case 'PageDown': setPos(pos - BIG); break;
            case 'PageUp': setPos(pos + BIG); break;
            case 'Home': setPos(0); break;
            case 'End': setPos(100); break;
            default: handled = false;
          }
          if (handled) e.preventDefault();
        });
        // The handle lives inside the draggable frame; suppress its own click so a
        // pointerdown-drag that ends on the knob doesn't fire a stray activation.
        handle.addEventListener('click', function (e) { e.preventDefault(); });
      }
    });
  }

/* [recommendations] initRecommendations */
/* ---------- product recommendations (fetch + inject, per-instance) ---------- */
  function initRecommendations(scope) {
    $all('[data-recommendations]', scope || document).forEach(function (root) {
      if (root.dataset.recLoaded === 'true') return;

      // Already rendered server-side (e.g. the request carried the intent params):
      // just reveal it and stop.
      if (root.querySelector('[data-rec-inner]')) {
        root.dataset.recLoaded = 'true';
        revealRecommendations(root);
        return;
      }

      var url = root.getAttribute('data-rec-url');
      if (!url) return; // no product context — nothing to fetch
      root.dataset.recLoaded = 'true';

      fetch(url)
        .then(function (r) { if (!r.ok) throw new Error('recommendations failed'); return r.text(); })
        .then(function (html) {
          var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rec-inner]');
          if (!incoming) return; // no recommendations — leave the section empty (no box)
          root.appendChild(incoming);
          revealRecommendations(incoming);
        })
        .catch(function () { /* graceful: section stays empty */ });
    });
  }

  /* Injected nodes are never seen by the reveal IntersectionObserver, so show them. */
  function revealRecommendations(container) {
    if (container.hasAttribute && container.hasAttribute('data-reveal')) {
      container.classList.add('is-revealed');
    }
    $all('[data-reveal]', container).forEach(function (n) { n.classList.add('is-revealed'); });
  }

/* [recently-viewed] localStorage historik */
/* ---------- recently viewed: the visitor's own product history ----------
   Handles only (never product data), newest first, de-duplicated, capped.
   Written on every product page from body[data-product-handle] (layout/theme.liquid).
   Private mode / disabled storage throws — every access is guarded, and the
   feature simply does not appear rather than breaking the page.
   RV_KEY / RV_CAP are declared at the top of this file — see the note there. */
  function rvRead() {
    try {
      var raw = window.localStorage.getItem(RV_KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list.filter(function (h) { return typeof h === 'string' && h.length > 0; });
    } catch (e) {
      return [];
    }
  }

  function recordRecentlyViewed() {
    var handle = document.body ? document.body.getAttribute('data-product-handle') : '';
    if (!handle) return; // not a product page — nothing to record
    var list = rvRead().filter(function (h) { return h !== handle; });
    list.unshift(handle);
    if (list.length > RV_CAP) list = list.slice(0, RV_CAP);
    try {
      window.localStorage.setItem(RV_KEY, JSON.stringify(list));
    } catch (e) { /* storage full or blocked: history just doesn't persist */ }
  }

/* [recently-viewed] initRecentlyViewed */
/* ---------- recently viewed: fetch real cards, or render nothing ----------
   Each handle is rendered server-side by sections/recently-viewed-card.liquid
   (Section Rendering API), so the markup is the shared 'product-card' snippet.
   The section stays hidden until at least one real card came back — there is
   never a placeholder. Deleted/unpublished products simply drop out. */
  function initRecentlyViewed(scope) {
    $all('[data-recently-viewed]', scope || document).forEach(function (root) {
      if (root.dataset.rvReady === 'true') return;
      root.dataset.rvReady = 'true';

      var grid = $('[data-rv-grid]', root);
      if (!grid) return;

      var current = root.getAttribute('data-rv-current') || '';
      var max = parseInt(root.getAttribute('data-rv-max'), 10) || 4;
      var base = (root.getAttribute('data-rv-base') || '/').replace(/\/+$/, '');

      var handles = rvRead().filter(function (h) { return h !== current; }).slice(0, max);
      if (!handles.length) return; // no history -> section stays hidden

      function load() {
        Promise.all(handles.map(function (handle) {
          var url = base + '/products/' + encodeURIComponent(handle) + '?section_id=recently-viewed-card';
          return fetch(url)
            .then(function (r) { return r.ok ? r.text() : ''; })
            .then(function (html) {
              if (!html) return null;
              return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rv-card]');
            })
            .catch(function () { return null; });
        })).then(function (nodes) {
          var added = 0;
          nodes.forEach(function (node) {
            if (!node) return;
            var item = document.createElement('li');
            item.className = 'geckio-rv__item';
            while (node.firstChild) item.appendChild(node.firstChild);
            grid.appendChild(item);
            added++;
          });
          if (!added) return; // every product is gone -> stay hidden, no empty box
          var note = $('[data-rv-note]', root);
          if (note && note.parentNode) note.parentNode.removeChild(note);
          root.removeAttribute('hidden');
          root.classList.add('is-loaded');
          // The section was display:none when the reveal observer ran, so reveal by hand.
          revealRecommendations(root);
        }).catch(function () { /* graceful: section stays hidden */ });
      }

      // Off the critical path: the history is a nice-to-have, never an LCP cost.
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(load, { timeout: 1500 });
      } else {
        window.setTimeout(load, 200);
      }
    });
  }

/* [stats] formatStatNumber */
/* ---------- stats: Danish number formatting ---------- */
  function formatStatNumber(value, decimals, group) {
    var neg = value < 0;
    value = Math.abs(value);
    var fixed = value.toFixed(decimals);
    var parts = fixed.split('.');
    var intPart = parts[0];
    if (group) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.'); /* Danish thousands separator = '.' */
    var out = intPart + (decimals > 0 ? ',' + parts[1] : ''); /* Danish decimal = ',' */
    return (neg ? '-' : '') + out;
  }

/* [stats] initStats */
/* ---------- stats: count-up on scroll into view ---------- */
  function initStats(scope) {
    $all('[data-geckio-stats]', scope || document).forEach(function (root) {
      if (root.dataset.statsReady === 'true') return;
      root.dataset.statsReady = 'true';

      var nums = $all('[data-stat-num]', root);
      if (!nums.length) return;

      var items = [];
      nums.forEach(function (el) {
        var raw = el.getAttribute('data-stat-value');
        var value = parseFloat((raw || '').replace(',', '.'));
        if (isNaN(value)) return;
        var decimals = 0;
        var dot = (raw || '').indexOf('.');
        if (dot !== -1) decimals = (raw.length - dot - 1);
        items.push({
          el: el,
          value: value,
          decimals: decimals,
          group: el.getAttribute('data-stat-group') === 'true'
        });
      });
      if (!items.length) return;

      function render(it, v) { it.el.textContent = formatStatNumber(v, it.decimals, it.group); }
      function finalAll() { items.forEach(function (it) { render(it, it.value); }); }

      /* No animation: show the real final numbers immediately. */
      if (reduceMotion.matches || !('IntersectionObserver' in window)) { finalAll(); return; }

      /* Start from zero, then count up once when scrolled into view. */
      items.forEach(function (it) { render(it, 0); });

      function run() {
        var duration = 1600;
        var start = null;
        function frame(ts) {
          if (start === null) start = ts;
          var p = Math.min(1, (ts - start) / duration);
          var eased = 1 - Math.pow(1 - p, 3); /* easeOutCubic */
          items.forEach(function (it) { render(it, it.value * eased); });
          if (p < 1) { window.requestAnimationFrame(frame); } else { finalAll(); }
        }
        window.requestAnimationFrame(frame);
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { io.disconnect(); run(); }
        });
      }, { threshold: 0.35 });
      io.observe(root);
    });
  }

/* [gallery] initGalleryLightbox */
/* ---------- image gallery: accessible lightbox (per-instance) ---------- */
  function initGalleryLightbox() {
    $all('[data-gallery-lightbox-root]').forEach(function (root) {
      if (root.dataset.glReady === 'true') return;
      root.dataset.glReady = 'true';

      var dialog = root.querySelector('[data-gallery-lightbox]');
      if (!dialog || typeof dialog.showModal !== 'function') return; // grid still works without JS/dialog
      var triggers = $all('[data-gallery-open]', root);
      if (!triggers.length) return;

      var inner = dialog.querySelector('.geckio-gallery__lb-inner');
      var img = dialog.querySelector('[data-gallery-lb-img]');
      var caption = dialog.querySelector('[data-gallery-lb-caption]');
      var counter = dialog.querySelector('[data-gallery-lb-counter]');
      var closeBtn = dialog.querySelector('[data-gallery-lb-close]');
      var prevBtn = dialog.querySelector('[data-gallery-lb-prev]');
      var nextBtn = dialog.querySelector('[data-gallery-lb-next]');
      var current = 0;
      var lastFocused = null;

      function render(i) {
        var t = triggers[i];
        if (!t) return;
        current = i;
        if (img) {
          img.src = t.getAttribute('data-full') || '';
          img.alt = t.getAttribute('data-alt') || '';
        }
        var cap = t.getAttribute('data-caption') || '';
        if (caption) {
          if (cap) { caption.textContent = cap; caption.hidden = false; }
          else { caption.textContent = ''; caption.hidden = true; }
        }
        if (counter) counter.textContent = (i + 1) + ' / ' + triggers.length;
      }
      function step(delta) {
        var n = (current + delta + triggers.length) % triggers.length;
        render(n);
      }
      function open(i) {
        lastFocused = document.activeElement;
        render(i);
        dialog.showModal(); // native focus-trap + Escape + background inert
      }
      function close() { if (dialog.open) dialog.close(); }

      triggers.forEach(function (t, i) {
        t.addEventListener('click', function () { open(i); });
      });
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

      // Click the backdrop or the empty grid gutter to dismiss.
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog || e.target === inner) close();
      });
      // Arrow keys page through; Escape is handled natively by <dialog>.
      dialog.addEventListener('keydown', function (e) {
        if (triggers.length < 2) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      });
      dialog.addEventListener('close', function () {
        if (img) img.removeAttribute('src');
        if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      });
    });
  }

/* [countdown] initCountdown */
function initCountdown(scope) {
    var prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    $all('[data-countdown]', scope || document).forEach(function (root) {
      if (root.dataset.countdownReady === 'true') return;
      root.dataset.countdownReady = 'true';

      var designMode = root.getAttribute('data-design-mode') === 'true';
      var mode = root.getAttribute('data-mode') || 'date';
      var target;

      if (mode === 'evergreen') {
        // Rolling per-visitor window: the deadline is minted once and kept in
        // localStorage per section, so it survives reloads but never restarts.
        var minutes = parseInt(root.getAttribute('data-minutes'), 10);
        if (!minutes || minutes <= 0) return;
        var span = minutes * 60000;
        if (designMode) {
          target = Date.now() + span; // editor: always show a running clock
        } else {
          var key = 'geckio:countdown:' + (root.getAttribute('data-key') || 'default');
          var stored = null;
          try { stored = window.localStorage.getItem(key); } catch (e) { stored = null; }
          var ts = parseInt(stored, 10);
          // Missing, unreadable, or further out than the current window (merchant
          // shortened it) -> mint a fresh deadline.
          if (!isFinite(ts) || ts > Date.now() + span) {
            ts = Date.now() + span;
            try { window.localStorage.setItem(key, String(ts)); } catch (e) { /* private mode */ }
          }
          target = ts;
        }
      } else {
        var raw = root.getAttribute('data-target');
        if (!raw) return;
        // Normalise "YYYY-MM-DD HH:MM" -> "YYYY-MM-DDTHH:MM" so it parses as LOCAL time.
        target = new Date(raw.trim().replace(' ', 'T')).getTime();
        if (isNaN(target)) return;
      }

      var live = root.querySelector('[data-countdown-live]');
      var expired = root.querySelector('[data-countdown-expired]');
      var clock = root.querySelector('[data-countdown-clock]');
      var elDays = root.querySelector('[data-countdown-days]');
      var elHours = root.querySelector('[data-countdown-hours]');
      var elMins = root.querySelector('[data-countdown-mins]');
      var elSecs = root.querySelector('[data-countdown-secs]');
      // Days are only rendered for windows of a day or more; when hidden they fold
      // into the hours so the row never shows a stale "00 Days".
      var showDays = root.getAttribute('data-show-days') === 'true' && !!elDays;
      var timer = null;
      var lastSecs = null;

      function pad(n) { return n < 10 ? '0' + n : '' + n; }
      function stop() { if (timer) { window.clearInterval(timer); timer = null; } }

      function showExpired() {
        // No expired message configured -> retire the whole section instead of
        // leaving an empty band behind.
        if (!expired) { root.hidden = true; return; }
        if (live) live.hidden = true;
        expired.hidden = false;
      }
      function showLive() {
        root.hidden = false;
        if (expired) expired.hidden = true;
        if (live) live.hidden = false;
      }
      function pulse(el) {
        if (!el || prefersReduce.matches) return;
        el.classList.remove('is-tick');
        void el.offsetWidth; // reflow so the animation can restart
        el.classList.add('is-tick');
      }

      function tick() {
        // Self-terminate if the section was removed (e.g. theme-editor re-render).
        if (!root.isConnected) { stop(); return; }

        var remain = target - Date.now();
        if (remain <= 0) {
          if (elDays) elDays.textContent = '00';
          if (elHours) elHours.textContent = '00';
          if (elMins) elMins.textContent = '00';
          if (elSecs) elSecs.textContent = '00';
          // In the editor, keep the clock visible so the merchant can still see it.
          if (designMode) { showLive(); } else { showExpired(); }
          stop();
          return;
        }

        showLive();
        var totalSec = Math.floor(remain / 1000);
        var days = showDays ? Math.floor(totalSec / 86400) : 0;
        var rest = showDays ? totalSec % 86400 : totalSec;
        var hours = Math.floor(rest / 3600);
        var mins = Math.floor((rest % 3600) / 60);
        var secs = rest % 60;

        if (elDays) elDays.textContent = pad(days);
        if (elHours) elHours.textContent = pad(hours);
        if (elMins) elMins.textContent = pad(mins);
        if (elSecs && secs !== lastSecs) { elSecs.textContent = pad(secs); pulse(elSecs); }
        lastSecs = secs;

        if (clock) {
          var parts = [];
          /* Every word here used to be an English literal, so the clock announced itself
             in English on a shop in any of the other nine languages. Both plural forms
             come from the layout's string bag; two forms cover every language this theme
             ships. The English literals stay as the last fallback, so a page served with
             a cached layout still says something rather than 'undefined'. */
          var T = window.geckioStrings || {};
          var unit = function (n, one, many) { return n + ' ' + (n === 1 ? one : many); };
          if (showDays && days) parts.push(unit(days, T.timeDay || 'day', T.timeDays || 'days'));
          if (hours) parts.push(unit(hours, T.timeHour || 'hour', T.timeHours || 'hours'));
          if (mins) parts.push(unit(mins, T.timeMinute || 'minute', T.timeMinutes || 'minutes'));
          if (!parts.length) parts.push(unit(secs, T.timeSecond || 'second', T.timeSeconds || 'seconds'));
          clock.setAttribute('aria-label', (T.timeLeft || '%% left on this offer').replace('%%', parts.join(', ')));
        }
      }

      tick();
      if (target - Date.now() > 0) {
        timer = window.setInterval(tick, 1000);
      }
    });
  }

/* [shoppable-image] initShoppable */
function initShoppable() {
    $all('[data-shoppable]').forEach(function (root) {
      if (root.dataset.shoppableReady === 'true') return;
      root.dataset.shoppableReady = 'true';

      var spots = $all('[data-shop-spot]', root);
      if (!spots.length) return;

      function closeSpot(spot) {
        spot.classList.remove('is-open');
        var btn = spot.querySelector('[data-shop-toggle]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }

      function closeAll(except) {
        spots.forEach(function (s) { if (s !== except) closeSpot(s); });
      }

      spots.forEach(function (spot) {
        var btn = spot.querySelector('[data-shop-toggle]');
        if (!btn) return;

        // Click pins the popover open (touch + persistent).
        btn.addEventListener('click', function () {
          var open = spot.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) closeAll(spot);
        });

        // Keyboard focus reveals the popover (mirrors the CSS :focus-within).
        spot.addEventListener('focusin', function () {
          btn.setAttribute('aria-expanded', 'true');
        });
        spot.addEventListener('focusout', function (e) {
          if (spot.contains(e.relatedTarget)) return;
          if (!spot.classList.contains('is-open')) btn.setAttribute('aria-expanded', 'false');
        });

        // Escape closes and returns focus to the dot.
        spot.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape' && e.key !== 'Esc') return;
          if (!spot.classList.contains('is-open') && !spot.contains(document.activeElement)) return;
          e.stopPropagation();
          closeSpot(spot);
          btn.focus();
        });
      });

      // Click anywhere outside a hotspot closes any pinned popover in this instance.
      document.addEventListener('click', function (e) {
        if (root.contains(e.target) && e.target.closest('[data-shop-spot]')) return;
        closeAll(null);
      });
    });
  }

/* [collection-grid] initLoadMore */
function initLoadMore() {
    $all('[data-collgrid]').forEach(function (root) {
      if (root.dataset.loadMoreReady === 'true') return;
      var grid = $('[data-collgrid-grid]', root);
      var link = $('[data-load-more]', root);
      if (!grid || !link) return; // nothing to enhance (no next page rendered)
      root.dataset.loadMoreReady = 'true';

      var sectionId = root.getAttribute('data-section-id');
      var status = $('[data-collgrid-status]', root);
      var busy = false;

      link.addEventListener('click', function (e) {
        if (!sectionId) return; // no section id -> let the link navigate to the next page
        var current = $('[data-load-more]', root);
        if (!current) return;
        var url = current.getAttribute('href');
        if (!url) return;
        e.preventDefault();
        if (busy) return;
        busy = true;
        current.setAttribute('aria-busy', 'true');
        var labelEl = current.querySelector('[data-load-more-label]');
        var prevText = labelEl ? labelEl.textContent : '';
        var loadingLabel = current.getAttribute('data-loading-label');
        if (labelEl && loadingLabel) labelEl.textContent = loadingLabel;

        var sep = url.indexOf('?') === -1 ? '?' : '&';
        fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId))
          .then(function (r) { if (!r.ok) throw new Error('load more failed'); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newGrid = doc.querySelector('[data-collgrid-grid]');
            var added = 0;
            if (newGrid) {
              $all('.geckio-collgrid__item', newGrid).forEach(function (item) {
                // Appended nodes are past the reveal observer, so show them immediately.
                item.classList.add('is-revealed');
                item.style.setProperty('--reveal-delay', '0s');
                grid.appendChild(item);
                added++;
              });
            }
            var newMore = doc.querySelector('[data-load-more]');
            var wrap = current.closest('[data-collgrid-more]');
            if (newMore) {
              current.setAttribute('href', newMore.getAttribute('href'));
              current.removeAttribute('aria-busy');
              if (labelEl) labelEl.textContent = prevText;
            } else if (wrap && wrap.parentNode) {
              wrap.parentNode.removeChild(wrap); // last page reached
            }
            if (status) status.textContent = ((window.geckioStrings||{}).moreLoaded || '%% flere produkter indlæst').replace('%%', added);
            busy = false;
          })
          .catch(function () {
            busy = false;
            window.location.href = url; // graceful fallback: navigate to the next page
          });
      });
    });
  }

/* [tabs] initTabs */
function initTabs() {
    $all('[data-geckio-tabs]').forEach(function (root) {
      if (root.dataset.tabsReady === 'true') return;
      root.dataset.tabsReady = 'true';

      var tabs = $all('[data-tab]', root);
      if (!tabs.length) return;

      function activate(tab, setFocus) {
        if (!tab) return;
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.setAttribute('tabindex', selected ? '0' : '-1');
          var panelId = t.getAttribute('aria-controls');
          var panel = panelId ? document.getElementById(panelId) : null;
          if (panel) panel.hidden = !selected;
        });
        if (setFocus) tab.focus();
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { activate(tab, false); });
        tab.addEventListener('keydown', function (e) {
          var idx = tabs.indexOf(tab);
          var next = null;
          switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown': next = tabs[(idx + 1) % tabs.length]; break;
            case 'ArrowLeft':
            case 'ArrowUp': next = tabs[(idx - 1 + tabs.length) % tabs.length]; break;
            case 'Home': next = tabs[0]; break;
            case 'End': next = tabs[tabs.length - 1]; break;
            default: return;
          }
          e.preventDefault();
          activate(next, true);
        });
      });

      // Normalise to a single valid selected state (honours a pre-marked tab, else the first).
      var current = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
      activate(current, false);
    });
  }

/* [slideshow] initSlideshow */
function initSlideshow() {
    $all('[data-slideshow]').forEach(function (root) {
      if (root.dataset.slideshowReady === 'true') return;
      root.dataset.slideshowReady = 'true';

      var track = $('[data-slideshow-track]', root);
      var slides = $all('[data-slideshow-slide]', root);
      if (!track || slides.length === 0) return;

      var count = slides.length;
      if (count < 2) { slides[0].removeAttribute('inert'); return; }

      var dots = $all('[data-slideshow-dot]', root);
      var prevBtn = $('[data-slideshow-prev]', root);
      var nextBtn = $('[data-slideshow-next]', root);
      var toggle = $('[data-slideshow-toggle]', root);
      var live = $('[data-slideshow-live]', root);

      var index = 0;
      var autoplay = root.getAttribute('data-autoplay') === 'true';
      var interval = Math.max(3, parseFloat(root.getAttribute('data-interval')) || 6) * 1000;
      var timer = null;
      var userPaused = false;

      function announce() {
        if (live) live.textContent = ((window.geckioStrings || {}).goToSlide || '%% / %#').replace('%%', index + 1).replace('%#', count);
      }

      function setActive(i) {
        index = i;
        slides.forEach(function (s, n) {
          if (n === i) { s.removeAttribute('inert'); } else { s.setAttribute('inert', ''); }
        });
        dots.forEach(function (d, n) {
          var on = n === i;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-current', on ? 'true' : 'false');
          d.setAttribute('tabindex', on ? '0' : '-1');
        });
      }

      function goTo(i, focusSlide) {
        i = (i % count + count) % count;
        var target = slides[i];
        track.scrollTo({ left: target.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        setActive(i);
        announce();
        if (focusSlide) {
          var f = target.querySelector('a[href], button:not([disabled])');
          if (f) f.focus();
        }
      }

      // Keep the active dot in sync while the visitor swipes / scrolls the track.
      var raf = null;
      track.addEventListener('scroll', function () {
        if (raf) window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(function () {
          var pos = track.scrollLeft, closest = 0, min = Infinity;
          slides.forEach(function (s, n) {
            var d = Math.abs(s.offsetLeft - pos);
            if (d < min) { min = d; closest = n; }
          });
          if (closest !== index) { setActive(closest); announce(); }
        });
      }, { passive: true });

      // ---- Autoplay (respects reduced-motion + user pause) ----
      function canAutoplay() { return autoplay && !userPaused && !reduceMotion.matches; }
      function startTimer() {
        if (!canAutoplay() || timer) return;
        timer = window.setInterval(function () { goTo(index + 1); }, interval);
      }
      function stopTimer() {
        if (timer) { window.clearInterval(timer); timer = null; }
      }
      function updateToggle() {
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        var lbl = userPaused ? toggle.getAttribute('data-label-play') : toggle.getAttribute('data-label-pause');
        if (lbl) toggle.setAttribute('aria-label', lbl);
        root.classList.toggle('is-paused', userPaused);
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          userPaused = !userPaused;
          if (userPaused) { stopTimer(); } else { startTimer(); }
          updateToggle();
        });
      }

      // Pause on hover / focus within the carousel (WCAG 2.2.2).
      root.addEventListener('mouseenter', stopTimer);
      root.addEventListener('mouseleave', startTimer);
      root.addEventListener('focusin', stopTimer);
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) startTimer();
      });

      // ---- Arrows ----
      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); });

      // ---- Dots: click + roving-tabindex arrow-key navigation ----
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { goTo(i); });
        dot.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % count;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + count) % count;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = count - 1;
          if (next === null) return;
          e.preventDefault();
          goTo(next);
          if (dots[next]) dots[next].focus();
        });
      });

      setActive(0);
      announce();
      updateToggle();
      startTimer();
    });
  }


  /* ===== Geckio section modules — bolge 2 (nye + layout-varianter) ===== */

/* [tabbed-collections] initTabbedCollections */
/* [tabbed-collections] initTabbedCollections */
function initTabbedCollections() {
    $all('[data-tabcoll]').forEach(function (root) {
      if (root.dataset.tabcollReady === 'true') return;
      root.dataset.tabcollReady = 'true';

      var tabs = $all('[data-tabcoll-tab]', root);
      var panels = $all('[data-tabcoll-panel]', root);
      var select = $('[data-tabcoll-select]', root);
      if (!tabs.length) return;

      function activate(index, setFocus) {
        if (index < 0 || index >= tabs.length) return;
        tabs.forEach(function (t, i) {
          var on = i === index;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        panels.forEach(function (p, i) { p.hidden = i !== index; });
        if (select && select.selectedIndex !== index) select.selectedIndex = index;
        if (setFocus && tabs[index]) tabs[index].focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(i, false); });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown': next = (i + 1) % tabs.length; break;
            case 'ArrowLeft':
            case 'ArrowUp': next = (i - 1 + tabs.length) % tabs.length; break;
            case 'Home': next = 0; break;
            case 'End': next = tabs.length - 1; break;
            default: return;
          }
          e.preventDefault();
          activate(next, true);
        });
      });

      if (select) {
        select.addEventListener('change', function () { activate(select.selectedIndex, false); });
      }

      // Normalise to a single valid selected state (honours a pre-marked tab, else the first).
      var current = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0];
      activate(current ? tabs.indexOf(current) : 0, false);
    });
  }

/* [background-video] initBackgroundVideo */
/* ---------- background video: lazy, reduced-motion aware (per-instance) ---------- */
  function initBackgroundVideo(scope) {
    $all('[data-bgvideo]', scope || document).forEach(function (root) {
      if (root.dataset.bgvReady === 'true') return;
      root.dataset.bgvReady = 'true';

      var kind = root.getAttribute('data-bgvideo-kind');
      var mount = root.querySelector('[data-bgvideo-mount]');
      if (!mount) return;

      /* Reduced motion: leave the poster in place, never autoplay. */
      if (reduceMotion.matches) return;

      var isEmbed = (kind === 'youtube' || kind === 'vimeo');
      var video = isEmbed ? null : mount.querySelector('video');
      if (!isEmbed && !video) return;
      var started = false;

      function buildEmbed() {
        var id = root.getAttribute('data-bgvideo-id');
        if (!id) return;
        var src;
        if (kind === 'youtube') {
          src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
            '?autoplay=1&mute=1&controls=0&loop=1&playlist=' + encodeURIComponent(id) +
            '&playsinline=1&modestbranding=1&rel=0&disablekb=1&fs=0&iv_load_policy=3';
        } else {
          src = 'https://player.vimeo.com/video/' + encodeURIComponent(id) +
            '?autoplay=1&muted=1&loop=1&background=1&playsinline=1';
        }
        var iframe = document.createElement('iframe');
        iframe.className = 'geckio-bgvideo__frame';
        iframe.src = src;
        iframe.title = root.getAttribute('data-bgvideo-title') || 'Baggrundsvideo';
        iframe.setAttribute('tabindex', '-1');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('loading', 'lazy');
        iframe.addEventListener('load', function () { root.classList.add('is-playing'); });
        mount.appendChild(iframe);
      }

      function play() {
        if (isEmbed) {
          if (!started) { started = true; buildEmbed(); }
          return;
        }
        started = true;
        video.muted = true; /* required for programmatic autoplay on mobile */
        var pr = video.play();
        if (pr && pr.catch) pr.catch(function () {});
        if (!video.paused) root.classList.add('is-playing');
      }

      if (!isEmbed) {
        video.addEventListener('playing', function () { root.classList.add('is-playing'); });
      }

      if (!('IntersectionObserver' in window)) { play(); return; }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            play();
            if (isEmbed) io.disconnect(); /* iframe stays mounted once built */
          } else if (!isEmbed && started && video && !video.paused) {
            video.pause(); /* save battery when scrolled away */
          }
        });
      }, { rootMargin: '200px 0px' });
      io.observe(root);
    });
  }

/* [testimonial-carousel] initTestimonialCarousel */
/* [testimonial-carousel] initTestimonialCarousel */
  /* ---------- testimonial carousel: scroll-snap track + autoplay ---------- */
  function initTestimonialCarousel() {
    $all('[data-tcar]').forEach(function (root) {
      if (root.dataset.tcarReady === 'true') return;
      root.dataset.tcarReady = 'true';

      var track = $('[data-tcar-track]', root);
      var slides = $all('[data-tcar-slide]', root);
      if (!track || slides.length === 0) return;

      var count = slides.length;
      if (count < 2) return;

      var dots = $all('[data-tcar-dot]', root);
      var prevBtn = $('[data-tcar-prev]', root);
      var nextBtn = $('[data-tcar-next]', root);
      var toggle = $('[data-tcar-toggle]', root);
      var live = $('[data-tcar-live]', root);

      var index = 0;
      var autoplay = root.getAttribute('data-autoplay') === 'true';
      var interval = Math.max(3, parseFloat(root.getAttribute('data-interval')) || 6) * 1000;
      var timer = null;
      var userPaused = false;

      function announce() {
        if (live) live.textContent = ((window.geckioStrings || {}).goToSlide || '%% / %#').replace('%%', index + 1).replace('%#', count);
      }

      function setActive(i) {
        index = i;
        dots.forEach(function (d, n) {
          var on = n === i;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-current', on ? 'true' : 'false');
          d.setAttribute('tabindex', on ? '0' : '-1');
        });
      }

      function goTo(i, focusSlide) {
        i = (i % count + count) % count;
        var target = slides[i];
        track.scrollTo({ left: target.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        setActive(i);
        announce();
        if (focusSlide) {
          var f = target.querySelector('a[href], button:not([disabled])');
          if (f) f.focus();
        }
      }

      // Keep the active dot in sync while the visitor swipes / scrolls the track.
      var raf = null;
      track.addEventListener('scroll', function () {
        if (raf) window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(function () {
          var pos = track.scrollLeft, closest = 0, min = Infinity;
          slides.forEach(function (s, n) {
            var d = Math.abs(s.offsetLeft - pos);
            if (d < min) { min = d; closest = n; }
          });
          if (closest !== index) { setActive(closest); announce(); }
        });
      }, { passive: true });

      // ---- Autoplay (respects reduced-motion + user pause) ----
      function canAutoplay() { return autoplay && !userPaused && !reduceMotion.matches; }
      function startTimer() {
        if (!canAutoplay() || timer) return;
        timer = window.setInterval(function () { goTo(index + 1); }, interval);
      }
      function stopTimer() {
        if (timer) { window.clearInterval(timer); timer = null; }
      }
      function updateToggle() {
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        var lbl = userPaused ? toggle.getAttribute('data-label-play') : toggle.getAttribute('data-label-pause');
        if (lbl) toggle.setAttribute('aria-label', lbl);
        root.classList.toggle('is-paused', userPaused);
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          userPaused = !userPaused;
          if (userPaused) { stopTimer(); } else { startTimer(); }
          updateToggle();
        });
      }

      // Pause on hover / focus within the carousel (WCAG 2.2.2).
      root.addEventListener('mouseenter', stopTimer);
      root.addEventListener('mouseleave', startTimer);
      root.addEventListener('focusin', stopTimer);
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) startTimer();
      });

      // ---- Arrows ----
      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); });

      // ---- Dots: click + roving-tabindex arrow-key navigation ----
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { goTo(i); });
        dot.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % count;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + count) % count;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = count - 1;
          if (next === null) return;
          e.preventDefault();
          goTo(next);
          if (dots[next]) dots[next].focus();
        });
      });

      setActive(0);
      announce();
      updateToggle();
      startTimer();
    });
  }

/* [sections/before-after.liquid] initBeforeAfterVertical */
/* [before-after] initBeforeAfterVertical */
  /* Vertical variant of the before/after comparison slider. Mirrors initBeforeAfter
     but resolves position from the Y axis (clientY / height). Only touches frames
     marked with the NEW hook [data-before-after-vertical]; the horizontal
     initBeforeAfter (which queries [data-before-after]) never sees these, and vice
     versa, so a frame is processed by exactly one initialiser. The same --ba-pos
     custom property, [data-ba-handle] element and .is-ready class are reused so the
     scoped CSS stays shared. */
  function initBeforeAfterVertical(scope) {
    $all('[data-before-after-vertical]', scope || document).forEach(function (frame) {
      if (frame.dataset.baReady === 'true') return;
      frame.dataset.baReady = 'true';

      var handle = frame.querySelector('[data-ba-handle]');
      var section = frame.closest('.geckio-ba');
      if (section) section.classList.add('is-ready');

      var STEP = 1, BIG = 10;
      var dragging = false;

      function clamp(v) { return Math.max(0, Math.min(100, v)); }
      function current() { return parseFloat(frame.style.getPropertyValue('--ba-pos')) || 50; }

      function setPos(v) {
        v = clamp(v);
        frame.style.setProperty('--ba-pos', v);
        if (handle) {
          var rounded = Math.round(v);
          handle.setAttribute('aria-valuenow', rounded);
          handle.setAttribute('aria-valuetext', rounded + '%');
        }
      }

      function posFromEvent(e) {
        var rect = frame.getBoundingClientRect();
        if (!rect.height) return;
        setPos((e.clientY - rect.top) / rect.height * 100);
      }

      frame.addEventListener('pointerdown', function (e) {
        dragging = true;
        frame.classList.add('is-dragging');
        if (frame.setPointerCapture) { try { frame.setPointerCapture(e.pointerId); } catch (err) {} }
        posFromEvent(e);
        if (handle) handle.focus();
      });
      frame.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        posFromEvent(e);
      });
      function endDrag() {
        dragging = false;
        frame.classList.remove('is-dragging');
      }
      frame.addEventListener('pointerup', endDrag);
      frame.addEventListener('pointercancel', endDrag);

      if (handle) {
        handle.addEventListener('keydown', function (e) {
          var pos = current(), handled = true;
          switch (e.key) {
            case 'ArrowUp': case 'ArrowLeft': setPos(pos - STEP); break;
            case 'ArrowDown': case 'ArrowRight': setPos(pos + STEP); break;
            case 'PageUp': setPos(pos - BIG); break;
            case 'PageDown': setPos(pos + BIG); break;
            case 'Home': setPos(0); break;
            case 'End': setPos(100); break;
            default: handled = false;
          }
          if (handled) e.preventDefault();
        });
        // Suppress the handle's own click so a pointerdown-drag that ends on the knob
        // doesn't fire a stray activation (matches the horizontal initialiser).
        handle.addEventListener('click', function (e) { e.preventDefault(); });
      }
    });
  }

/* [sections/recommendations.liquid] initRecCarousel */
/* [recommendations] initRecCarousel — arrow controls for the 'karrusel med pile'
     layout. CSS-only scroll-snap handles touch/scrollbar; this only wires the
     prev/next buttons. Because the recommendations markup is normally injected
     async by initRecommendations() (which is NOT touched here), we detect the
     layout from the section root class (present at first paint) and, when the
     [data-rec-inner] block is not yet present, wait for it via a scoped
     MutationObserver. Fully idempotent + per-instance. */
  function initRecCarousel(scope) {
    $all('[data-recommendations].geckio-recs--layout-karrusel', scope || document).forEach(function (root) {
      if (root.dataset.recCarousel === 'ready') return;

      function wire(inner) {
        var track = inner.querySelector('[data-rec-track]');
        if (!track) return;
        root.dataset.recCarousel = 'ready';

        var prev = inner.querySelector('[data-rec-prev]');
        var next = inner.querySelector('[data-rec-next]');
        var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

        function step(dir) {
          var item = track.querySelector('.geckio-recs__item');
          var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
          var amount = item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
          var reduce = mq && mq.matches;
          track.scrollBy({ left: dir * amount, behavior: reduce ? 'auto' : 'smooth' });
        }

        function update() {
          var max = track.scrollWidth - track.clientWidth - 1;
          if (prev) prev.disabled = track.scrollLeft <= 0;
          if (next) next.disabled = track.scrollLeft >= max;
        }

        if (prev) prev.addEventListener('click', function () { step(-1); });
        if (next) next.addEventListener('click', function () { step(1); });
        track.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
      }

      var present = root.querySelector('[data-rec-inner]');
      if (present) { wire(present); return; }

      var mo = new MutationObserver(function () {
        var inner = root.querySelector('[data-rec-inner]');
        if (inner) { mo.disconnect(); wire(inner); }
      });
      mo.observe(root, { childList: true });
    });
  }


  /* ===== Geckio product page (main-product) ===== */

/* ============================================================
   Geckio — sections/main-product.liquid  (initGeckioSections modules)
   Merge these two function declarations into the theme.js IIFE and add
   initProductGallery() + initProductForm() to the initGeckioSections()
   dispatcher (which already runs on boot AND on shopify:section:load).

   They rely on helpers already defined in theme.js:
     $, $all, addToCart, reduceMotion
   Both are idempotent, per-instance (guarded by a dataset flag) and scoped
   to their own section root, so a full-document re-scan is safe to re-run.
   ============================================================ */

function initProductGallery() {
    $all('[data-mp-gallery]').forEach(function (gallery) {
      if (gallery.dataset.mpGalleryReady === 'true') return;
      gallery.dataset.mpGalleryReady = 'true';

      var root = gallery.closest('[data-mainproduct]') || gallery;
      var track = gallery.querySelector('[data-mp-track]');
      var slides = $all('[data-mp-slide]', gallery);
      var thumbs = $all('[data-mp-thumb]', gallery);
      var dots = $all('[data-mp-dot]', gallery);
      var prevBtn = gallery.querySelector('[data-mp-prev]');
      var nextBtn = gallery.querySelector('[data-mp-next]');
      var current = 0;

      function indexOfMedia(id) {
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === String(id)) return i;
        }
        return -1;
      }

      function updateIndicators(i) {
        if (i < 0 || i >= slides.length) return;
        current = i;
        slides.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
        thumbs.forEach(function (t, n) {
          var on = n === i;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        dots.forEach(function (d, n) {
          var on = n === i;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      function updateArrows() {
        if (!track) return;
        var max = track.scrollWidth - track.clientWidth - 1;
        if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
        if (nextBtn) nextBtn.disabled = track.scrollLeft >= max;
      }

      function goTo(i, smooth) {
        if (i < 0 || i >= slides.length) return;
        if (track && slides[i]) {
          var left = track.scrollLeft + (slides[i].getBoundingClientRect().left - track.getBoundingClientRect().left);
          track.scrollTo({ left: left, behavior: (smooth && !reduceMotion.matches) ? 'smooth' : 'auto' });
        } else if (slides[i]) {
          slides[i].scrollIntoView({ block: 'nearest', behavior: (smooth && !reduceMotion.matches) ? 'smooth' : 'auto' });
        }
        updateIndicators(i);
      }

      /* thumbnails: click + roving-tabindex arrow navigation */
      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () { goTo(i, true); });
        thumb.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % thumbs.length;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + thumbs.length) % thumbs.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = thumbs.length - 1;
          if (next === null) return;
          e.preventDefault();
          goTo(next, true);
          if (thumbs[next]) thumbs[next].focus();
        });
      });

      /* dots */
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { goTo(i, true); });
      });

      /* arrows */
      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(Math.max(0, current - 1), true); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(Math.min(slides.length - 1, current + 1), true); });

      /* keep the active indicator in sync while swiping/scrolling the track */
      if (track && slides.length > 1) {
        var raf = null;
        track.addEventListener('scroll', function () {
          if (raf) window.cancelAnimationFrame(raf);
          raf = window.requestAnimationFrame(function () {
            var trackLeft = track.getBoundingClientRect().left;
            var closest = 0, min = Infinity;
            slides.forEach(function (s, n) {
              var d = Math.abs(s.getBoundingClientRect().left - trackLeft);
              if (d < min) { min = d; closest = n; }
            });
            if (closest !== current) updateIndicators(closest);
            updateArrows();
          });
        }, { passive: true });
        window.addEventListener('resize', updateArrows);
      }

      /* external video facades: build the iframe on demand (no autoload) */
      $all('[data-mp-extvideo]', gallery).forEach(function (wrap) {
        var btn = wrap.querySelector('[data-mp-extvideo-play]');
        if (!btn) return;
        btn.addEventListener('click', function () {
          if (wrap.dataset.built === 'true') return;
          wrap.dataset.built = 'true';
          var host = wrap.getAttribute('data-video-host');
          var id = wrap.getAttribute('data-video-id');
          if (!id) return;
          var src;
          if (host === 'youtube') {
            src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1';
          } else {
            src = 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1&playsinline=1';
          }
          var iframe = document.createElement('iframe');
          iframe.className = 'geckio-mainproduct__extframe';
          iframe.src = src;
          iframe.title = (window.geckioStrings || {}).video || 'Video';
          iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
          iframe.setAttribute('allowfullscreen', '');
          wrap.appendChild(iframe);
          wrap.classList.add('is-playing');
        });
      });

      /* accessible zoom / lightbox (native <dialog>) */
      (function () {
        var dialog = root.querySelector('[data-mp-lightbox]');
        var triggers = $all('[data-mp-zoom]', gallery);
        if (!dialog || typeof dialog.showModal !== 'function' || !triggers.length) return;

        var img = dialog.querySelector('[data-mp-lb-img]');
        var counter = dialog.querySelector('[data-mp-lb-counter]');
        var inner = dialog.querySelector('[data-mp-lb-inner]');
        var closeBtn = dialog.querySelector('[data-mp-lb-close]');
        var prevLb = dialog.querySelector('[data-mp-lb-prev]');
        var nextLb = dialog.querySelector('[data-mp-lb-next]');
        var lbIndex = 0;
        var lastFocused = null;

        var single = triggers.length < 2;
        if (single) {
          if (prevLb) prevLb.hidden = true;
          if (nextLb) nextLb.hidden = true;
          if (counter) counter.hidden = true;
        }

        function render(i) {
          var t = triggers[i];
          if (!t) return;
          lbIndex = i;
          if (img) { img.src = t.getAttribute('data-full') || ''; img.alt = t.getAttribute('data-alt') || ''; }
          if (counter && !single) counter.textContent = (i + 1) + ' / ' + triggers.length;
        }
        function step(delta) { render((lbIndex + delta + triggers.length) % triggers.length); }
        function open(i) { lastFocused = document.activeElement; render(i); dialog.showModal(); }
        function close() { if (dialog.open) dialog.close(); }

        triggers.forEach(function (t, i) { t.addEventListener('click', function () { open(i); }); });
        if (closeBtn) closeBtn.addEventListener('click', close);
        if (prevLb) prevLb.addEventListener('click', function () { step(-1); });
        if (nextLb) nextLb.addEventListener('click', function () { step(1); });
        dialog.addEventListener('click', function (e) { if (e.target === dialog || e.target === inner) close(); });
        dialog.addEventListener('keydown', function (e) {
          if (triggers.length < 2) return;
          if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        });
        dialog.addEventListener('close', function () {
          if (img) img.removeAttribute('src');
          if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
        });
      })();

      /* hover-zoom: track the pointer so the image scales toward the cursor.
         Mouse only — on touch the "hover" state would stick after a tap. */
      $all('[data-mp-hoverzoom]', gallery).forEach(function (fig) {
        fig.addEventListener('pointermove', function (e) {
          if (e.pointerType && e.pointerType !== 'mouse') return;
          var r = fig.getBoundingClientRect();
          if (!r.width || !r.height) return;
          fig.style.setProperty('--mp-zoom-x', ((e.clientX - r.left) / r.width * 100).toFixed(2) + '%');
          fig.style.setProperty('--mp-zoom-y', ((e.clientY - r.top) / r.height * 100).toFixed(2) + '%');
        });
        fig.addEventListener('pointerleave', function () {
          fig.style.removeProperty('--mp-zoom-x');
          fig.style.removeProperty('--mp-zoom-y');
        });
      });

      /* variant media filtering: show only the media the selected variant owns.
         Media owned by no variant is shared and always stays visible. If the
         variant owns nothing at all, everything is shown again (graceful fallback). */
      var filterOn = gallery.getAttribute('data-variant-filter') === 'true';
      var filterables = slides.concat(thumbs).concat(dots);

      function ownedBy(el, id) {
        var ids = el.getAttribute('data-variant-ids');
        if (!ids) return null; // shared
        return ids.split(',').indexOf(id) > -1;
      }

      function filterByVariant(variantId) {
        if (!filterOn || variantId == null) return;
        var id = String(variantId);
        var hasOwn = slides.some(function (s) { return ownedBy(s, id) === true; });

        function run() {
          filterables.forEach(function (el) {
            var owns = ownedBy(el, id);
            // no own media -> show all; shared media (null) always visible
            el.classList.toggle('is-filtered-out', hasOwn && owns === false);
          });
          var firstVisible = slides.filter(function (s) { return !s.classList.contains('is-filtered-out'); })[0];
          if (firstVisible) goTo(slides.indexOf(firstVisible), false);
          updateArrows();
        }

        if (reduceMotion.matches) { run(); return; }
        gallery.classList.add('is-crossfading');
        window.setTimeout(function () {
          run();
          window.requestAnimationFrame(function () { gallery.classList.remove('is-crossfading'); });
        }, 160);
      }

      /* variant change -> filter the media, then focus the variant's featured media */
      root.addEventListener('geckio:media:select', function (e) {
        var detail = e.detail || {};
        if (detail.variantId != null) filterByVariant(detail.variantId);
        if (detail.mediaId == null) return;
        var idx = indexOfMedia(detail.mediaId);
        if (idx > -1 && !slides[idx].classList.contains('is-filtered-out')) goTo(idx, true);
      });

      updateIndicators(0);
      updateArrows();
    });
  }

function initProductForm() {
    $all('.geckio-mainproduct__form').forEach(function (form) {
      if (form.dataset.mpFormReady === 'true') return;
      form.dataset.mpFormReady = 'true';

      var root = form.closest('[data-mainproduct]') || form;
      var idInput = form.querySelector('[data-variant-id]');

      var variants = [];
      var dataEl = root.querySelector('[data-mp-variants]');
      if (dataEl) { try { variants = JSON.parse(dataEl.textContent); } catch (err) { variants = []; } }

      var controls = $all('[data-mp-option]', form);
      var priceNow = root.querySelector('[data-mp-price-now]');
      var priceWas = root.querySelector('[data-mp-price-was]');
      var priceSave = root.querySelector('[data-mp-price-save]');
      var priceWrap = root.querySelector('[data-mp-price]');
      var unitWrap = root.querySelector('[data-mp-unit]');
      var unitVal = root.querySelector('[data-mp-unit-value]');
      var addBtn = form.querySelector('[data-mp-atc]');
      var addText = addBtn ? addBtn.querySelector('[data-mp-atc-text]') : null;
      var errEl = root.querySelector('[data-mp-error]');
      var qtyInput = form.querySelector('[data-mp-qty-input]');

      var stickyBar = root.querySelector('[data-mp-stickybar]');
      var stickyBtn = stickyBar ? stickyBar.querySelector('[data-mp-sticky-atc]') : null;
      var stickyPrice = stickyBar ? stickyBar.querySelector('[data-mp-sticky-price]') : null;
      var stickyVariant = stickyBar ? stickyBar.querySelector('[data-mp-sticky-variant]') : null;

      var addLabel = addBtn ? (addBtn.getAttribute('data-label-add') || (window.geckioStrings||{}).addToCart || 'Læg i kurv') : 'Læg i kurv';
      var soldLabel = addBtn ? (addBtn.getAttribute('data-label-soldout') || 'Udsolgt') : 'Udsolgt';
      var unavailLabel = addBtn ? (addBtn.getAttribute('data-label-unavailable') || (window.geckioStrings||{}).unavailable || 'Ikke tilgængelig') : 'Ikke tilgængelig';

      function currentSelection() {
        var sel = [];
        controls.forEach(function (c) {
          var pos = parseInt(c.getAttribute('data-option-position'), 10);
          if (isNaN(pos)) return;
          if (c.tagName === 'SELECT') { sel[pos] = c.value; }
          else if (c.checked) { sel[pos] = c.value; }
        });
        return sel;
      }

      function findVariant(sel) {
        for (var i = 0; i < variants.length; i++) {
          var ok = true;
          for (var p = 0; p < sel.length; p++) {
            if (sel[p] != null && variants[i].options[p] !== sel[p]) { ok = false; break; }
          }
          if (ok) return variants[i];
        }
        return null;
      }

      /* 'available' | 'soldout' (exists but no stock) | 'none' (combination does not exist) */
      function stateFor(pos, val, sel) {
        var exists = false, avail = false;
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].options[pos] !== val) continue;
          var ok = true;
          for (var p = 0; p < sel.length; p++) {
            if (p !== pos && sel[p] != null && variants[i].options[p] !== sel[p]) { ok = false; break; }
          }
          if (!ok) continue;
          exists = true;
          if (variants[i].available) { avail = true; break; }
        }
        return avail ? 'available' : (exists ? 'soldout' : 'none');
      }

      function updateStates(sel) {
        controls.forEach(function (c) {
          var pos = parseInt(c.getAttribute('data-option-position'), 10);
          if (isNaN(pos)) return;
          if (c.tagName === 'SELECT') {
            $all('option', c).forEach(function (o) {
              var st = stateFor(pos, o.value, sel);
              o.classList.toggle('is-soldout', st !== 'available');
              // never disable the currently selected value (avoids dead-ends)
              o.disabled = (st === 'none' && o.value !== c.value);
            });
          } else {
            var st = stateFor(pos, c.value, sel);
            /* Both names: the shared snippet marks its labels data-geckio-vp-label, and any
               hand-rolled markup still out there uses the old one. parentNode remains the
               last resort because the input sits inside its own <label> either way. */
            var label =
              c.closest('[data-geckio-vp-label]') ||
              c.closest('[data-mp-option-label]') ||
              c.parentNode;
            if (label) {
              label.classList.toggle('is-soldout', st !== 'available');
              label.classList.toggle('is-none', st === 'none');
            }
          }
        });
      }

      function applyVariant(v) {
        if (idInput) {
          idInput.value = v ? v.id : '';
          idInput.disabled = !(v && v.available); // keep native no-JS post safe
        }
        if (priceNow && v) priceNow.innerHTML = v.price;
        if (priceWas) {
          if (v && v.compare_at) { priceWas.innerHTML = v.compare_at; priceWas.hidden = false; }
          else { priceWas.hidden = true; }
        }
        if (priceSave) {
          if (v && v.compare_at_amount && v.price_amount && v.compare_at_amount > v.price_amount) {
            var pct = Math.round((v.compare_at_amount - v.price_amount) / v.compare_at_amount * 100);
            priceSave.textContent = ((window.geckioStrings||{}).savePercent || 'Spar %%%').replace('%%', pct);
            priceSave.hidden = false;
          } else { priceSave.hidden = true; }
        }
        if (priceWrap) {
          priceWrap.classList.toggle('is-sale', !!(v && v.compare_at_amount && v.price_amount && v.compare_at_amount > v.price_amount));
        }
        if (unitWrap) {
          if (v && v.unit_price) {
            if (unitVal) unitVal.innerHTML = v.unit_price + (v.unit_reference ? ' / ' + v.unit_reference : '');
            unitWrap.hidden = false;
          } else { unitWrap.hidden = true; }
        }
        if (addBtn) {
          if (!v) { addBtn.disabled = true; if (addText) addText.textContent = unavailLabel; }
          else if (v.available) { addBtn.disabled = false; if (addText) addText.textContent = addLabel; }
          else { addBtn.disabled = true; if (addText) addText.textContent = soldLabel; }
        }
        if (stickyPrice && v) stickyPrice.innerHTML = v.price;
        if (stickyVariant && v) stickyVariant.textContent = v.title && v.title !== 'Default Title' ? v.title : '';
        if (stickyBtn) stickyBtn.disabled = !(v && v.available);
        if (v) {
          root.dispatchEvent(new CustomEvent('geckio:media:select', {
            detail: { mediaId: v.media_id, variantId: v.id }
          }));
        }
        updateURL(v);
      }

      function variantById(id) {
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(id)) return variants[i];
        }
        return null;
      }

      function updateURL(v) {
        if (!v || !window.history || !window.history.replaceState) return;
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        } catch (err) { /* no-op */ }
      }

      function onChange() {
        var sel = currentSelection();
        updateStates(sel);
        applyVariant(findVariant(sel));
      }
      controls.forEach(function (c) { c.addEventListener('change', onChange); });

      /* offer cards (quantity bundles): each tier IS a real variant, so selecting a
         tier is just selecting that variant — price, compare-at and savings can never
         disagree with what checkout charges. */
      var offerInputs = $all('[data-mp-offer-input]', form);
      offerInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          offerInputs.forEach(function (other) {
            var label = other.closest('[data-mp-offer]');
            if (label) label.classList.toggle('is-selected', other === input);
          });
          applyVariant(variantById(input.value));
        });
      });

      /* sticky ATC bar: appears once the real add-to-cart button leaves the viewport */
      if (stickyBar && stickyBtn) {
        stickyBtn.addEventListener('click', function () {
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
        var anchor = form.querySelector('[data-mp-atc-anchor]');
        if (anchor && 'IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              stickyBar.classList.toggle('is-visible', show);
              stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
            });
          }, { rootMargin: '0px 0px -10% 0px' });
          io.observe(anchor);
        }
      }

      /* quantity stepper */
      $all('[data-mp-qty-step]', form).forEach(function (step) {
        step.addEventListener('click', function () {
          if (!qtyInput) return;
          var delta = parseInt(step.getAttribute('data-mp-qty-step'), 10) || 0;
          var next = (parseInt(qtyInput.value, 10) || 1) + delta;
          qtyInput.value = Math.max(1, next);
        });
      });

      /* submit -> reuse the theme's AJAX cart + cart drawer */
      form.addEventListener('submit', function (e) {
        if (!idInput || !idInput.value || idInput.disabled) return; // sold out / no variant: let native handle
        e.preventDefault();
        if (form.dataset.submitting === 'true') return;
        form.dataset.submitting = 'true';
        var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
        if (addBtn) { addBtn.setAttribute('aria-busy', 'true'); addBtn.classList.add('is-loading'); }
        if (errEl) errEl.hidden = true;
        addToCart([{ id: idInput.value, quantity: qty }], function (err) {
          form.dataset.submitting = 'false';
          if (addBtn) { addBtn.removeAttribute('aria-busy'); addBtn.classList.remove('is-loading'); }
          if (err && errEl) errEl.hidden = false;
        });
      });

      /* initial paint: reconcile availability + variant state.
         With offer cards the checked tier is the source of truth, not the option controls. */
      (function () {
        var sel = currentSelection();
        updateStates(sel);
        var checkedOffer = offerInputs.filter(function (i) { return i.checked; })[0];
        applyVariant(checkedOffer ? variantById(checkedOffer.value) : findVariant(sel));
      })();
    });
  }

/* [main-collection] initCollectionFilters
   Storefront filters + sorting. The Liquid already works with JavaScript off —
   every filter value is a real link (value.url_to_add / url_to_remove) and both
   sort and price are real GET forms. This layer upgrades the same markup to
   Section Rendering API swaps with history push/pop, plus a mobile drawer.
   Anything that goes wrong falls back to a normal page load. */
function initCollectionFilters(scope) {
    $all('[data-collection-filters]', scope || document).forEach(function (root) {
      if (root.dataset.filtersReady === 'true') return;
      var sectionId = root.getAttribute('data-section-id');
      if (!sectionId) return; // without an id we cannot re-render — links keep working
      root.dataset.filtersReady = 'true';

      var panel = $('[data-filters-panel]', root);
      var toggle = $('[data-filters-toggle]', root);
      var status = $('[data-results-status]', root);
      var desktop = window.matchMedia('(min-width: 1024px)');
      var basePath = window.location.pathname;
      var token = 0;
      var lastFocused = null;

      // Flags the section as enhanced: only now does the CSS turn the in-flow
      // filter panel into a drawer, so the no-JS layout is never broken.
      root.setAttribute('data-enhanced', 'true');
      if (toggle) toggle.hidden = false;

      /* ---------- mobile drawer ---------- */
      var inner = panel ? $('.geckio-collection__panel-inner', panel) : null;
      var panelTitle = panel ? $('.geckio-collection__panel-title', panel) : null;

      function isOpen() { return !!panel && panel.classList.contains('is-open'); }

      function openDrawer() {
        if (!panel || desktop.matches || isOpen()) return;
        lastFocused = document.activeElement;
        panel.classList.add('is-open');
        document.body.classList.add('no-scroll');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
        // Only a real overlay is a dialog — the desktop sidebar must stay plain markup.
        if (inner) {
          inner.setAttribute('role', 'dialog');
          inner.setAttribute('aria-modal', 'true');
          if (panelTitle && panelTitle.id) inner.setAttribute('aria-labelledby', panelTitle.id);
        }
        var first = $('[data-filters-close]:not(.geckio-collection__scrim)', panel);
        if (first && first.focus) first.focus();
      }

      function closeDrawer(returnFocus) {
        if (!panel || !isOpen()) return;
        panel.classList.remove('is-open');
        document.body.classList.remove('no-scroll');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        if (inner) {
          inner.removeAttribute('role');
          inner.removeAttribute('aria-modal');
          inner.removeAttribute('aria-labelledby');
        }
        if (returnFocus === false) return;
        var back = (toggle && toggle.offsetParent !== null) ? toggle : lastFocused;
        if (back && back.focus) back.focus();
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          if (isOpen()) { closeDrawer(); } else { openDrawer(); }
        });
      }
      if (panel) {
        panel.addEventListener('click', function (e) {
          if (!e.target.closest('[data-filters-close]')) return;
          e.preventDefault();
          closeDrawer();
        });
      }
      /* ---------- horizontal bar layout: groups behave as dropdowns ---------- */
      function closeGroups(except) {
        if (!desktop.matches || !root.classList.contains('geckio-collection--fl-bar')) return;
        $all('[data-filter-group]', root).forEach(function (g) { if (g !== except) g.open = false; });
      }
      root.addEventListener('click', function (e) { closeGroups(e.target.closest('[data-filter-group]')); });
      document.addEventListener('click', function (e) {
        if (!root.isConnected || root.contains(e.target)) return;
        closeGroups(null);
      });

      document.addEventListener('keydown', function (e) {
        if (!root.isConnected) return;
        if (isOpen()) {
          if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
          if (e.key === 'Tab') trapFocus($('.geckio-collection__panel-inner', panel) || panel, e);
          return;
        }
        if (e.key === 'Escape') closeGroups(null);
      });
      if (desktop.addEventListener) {
        desktop.addEventListener('change', function () { if (desktop.matches) closeDrawer(false); });
      }

      /* ---------- section re-render ---------- */
      function announce(text) { if (status) status.textContent = text; }

      function swapHtml(doc, sel) {
        var next = doc.querySelector(sel);
        var cur = root.querySelector(sel);
        if (next && cur) cur.innerHTML = next.innerHTML;
      }
      function copyText(doc, sel) {
        var next = doc.querySelector(sel);
        var cur = root.querySelector(sel);
        if (next && cur) cur.textContent = next.textContent;
      }

      // The re-rendered filter markup arrives with Liquid's default open/closed
      // state, so remember what the visitor had expanded and put it back.
      function readGroups() {
        return $all('[data-filter-group]', root).map(function (g) { return g.open; });
      }
      function restoreGroups(state) {
        var groups = $all('[data-filter-group]', root);
        if (groups.length !== state.length) return;
        groups.forEach(function (g, i) { g.open = state[i]; });
      }

      // Focus dies with the swapped-out node. Filter links keep their order
      // across renders (only the active flag flips), so the index maps 1:1.
      function readFocus() {
        var active = document.activeElement;
        if (!active || !root.contains(active)) return null;
        var region = $('[data-filters-region]', root);
        if (region && region.contains(active)) {
          return { where: 'filters', index: $all('[data-filter-link]', region).indexOf(active) };
        }
        if (active.closest('[data-results-region]')) return { where: 'results' };
        return null;
      }
      function restoreFocus(mark) {
        if (!mark) return;
        if (mark.where === 'filters' && mark.index > -1) {
          var region = $('[data-filters-region]', root);
          var link = region ? $all('[data-filter-link]', region)[mark.index] : null;
          if (link) { link.focus(); return; }
        }
        var results = $('[data-results-region]', root);
        if (results) results.focus();
      }

      // Builds the URL a <form> would have produced on a normal GET submit.
      function serialize(form) {
        var params = [];
        $all('input, select', form).forEach(function (el) {
          if (!el.name || el.disabled || el.value === '') return;
          if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
          params.push(encodeURIComponent(el.name) + '=' + encodeURIComponent(el.value));
        });
        var action = form.getAttribute('action') || window.location.pathname;
        return params.length ? action + '?' + params.join('&') : action;
      }

      function render(url, opts) {
        opts = opts || {};
        var mine = ++token;
        var region = $('[data-results-region]', root);
        if (region) region.setAttribute('aria-busy', 'true');
        announce((window.geckioStrings||{}).updating || 'Opdaterer produkter …');

        var sep = url.indexOf('?') === -1 ? '?' : '&';
        fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId))
          .then(function (r) { if (!r.ok) throw new Error('filter fetch failed'); return r.text(); })
          .then(function (html) {
            if (mine !== token) return; // a newer click already won
            var doc = new DOMParser().parseFromString(html, 'text/html');
            if (!doc.querySelector('[data-results-region]')) throw new Error('filter response empty');

            var groups = readGroups();
            var focusMark = readFocus();

            swapHtml(doc, '[data-results-region]');
            swapHtml(doc, '[data-filters-region]');
            swapHtml(doc, '[data-sort-state]'); // keeps the sort form carrying the new filters
            swapHtml(doc, '[data-search-state]'); // and the search box carrying the new filters
            copyText(doc, '[data-results-count]');
            copyText(doc, '[data-filter-count]');
            copyText(doc, '[data-apply-count]');

            var incomingSort = doc.querySelector('[data-sort-select]');
            var sortSelect = $('[data-sort-select]', root);
            if (incomingSort && sortSelect && sortSelect.value !== incomingSort.value) {
              sortSelect.value = incomingSort.value;
            }

            if (opts.push !== false) {
              try { window.history.pushState({ geckioCollection: sectionId }, '', url); } catch (err) { /* no history access */ }
            }

            var updated = $('[data-results-region]', root);
            if (updated) updated.setAttribute('aria-busy', 'false');
            restoreGroups(groups);
            restoreFocus(focusMark);
            initReveal(); // observe the freshly injected cards

            var count = $('[data-results-count]', root);
            announce(count ? count.textContent : ((window.geckioStrings || {}).productsUpdated || 'Produkterne er opdateret'));

            if (opts.scroll) {
              var top = root.getBoundingClientRect().top + window.pageYOffset - 24;
              window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
            }
          })
          .catch(function () {
            if (mine !== token) return;
            window.location.href = url; // graceful fallback: a normal page load
          });
      }

      /* ---------- wiring ---------- */
      function isPlainClick(e) {
        return !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) && e.button === 0;
      }

      root.addEventListener('click', function (e) {
        var link = e.target.closest('[data-filter-link], [data-clear-all]');
        if (link) {
          if (link.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
          var href = link.getAttribute('href');
          if (!href || !isPlainClick(e)) return;
          e.preventDefault();
          render(href, { scroll: false });
          return;
        }
        var page = e.target.closest('[data-collection-pagination] a');
        if (!page) return;
        var pageHref = page.getAttribute('href');
        if (!pageHref || !isPlainClick(e)) return;
        e.preventDefault();
        closeDrawer(false);
        render(pageHref, { scroll: true });
      });

      // The price form lives inside the swapped region, so it has to be delegated.
      root.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-price-form]');
        if (!form) return;
        e.preventDefault();
        render(serialize(form), { scroll: false });
      });

      var sortForm = $('[data-sort-form]', root);
      if (sortForm) {
        var sortSelect = $('[data-sort-select]', sortForm);
        if (sortSelect) {
          sortSelect.addEventListener('change', function () { render(serialize(sortForm), { scroll: true }); });
        }
        sortForm.addEventListener('submit', function (e) {
          e.preventDefault();
          render(serialize(sortForm), { scroll: true });
        });
      }

      window.addEventListener('popstate', function () {
        if (!root.isConnected || window.location.pathname !== basePath) return;
        closeDrawer(false);
        render(window.location.href, { push: false, scroll: false });
      });
    });
  }

  /* Single dispatcher for the new-section modules, wired into boot + section:load.
     Every initX below is idempotent + per-instance (guarded by a dataset flag and
     scoped to its own root), so a full-document re-scan on section:load is safe. */
  function initGeckioSections(scope) {
    initSlideshow();
    initBeforeAfter();
    initTabs();
    initCountdown(scope);
    initShoppable();
    initLoadMore();
    initStats(scope);
    initRecommendations(scope);
    initRecentlyViewed(scope);
    initGalleryLightbox();
    initTabbedCollections();
    initBackgroundVideo(scope);
    initTestimonialCarousel();
    initBeforeAfterVertical();
    initRecCarousel();
    initProductGallery();
    initProductForm();
    initCollectionFilters(scope);
  }

  /* Re-init when sections are edited in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    initStickyHeader();
    syncHeaderHeight();
    initMenuDrawer();
    initPredictiveSearch();
    initCart();
    initAnnouncement(e.target);
    initBuy();
    initGallery();
    initStickyAtc();
    initGeckioProduct();
    initQuickView();
    initMegaMenu();
    initSizeGuide();
    initMarquee();
    initReveal();
    initGeckioSections(e.target);
  });
})();
